import React, { useState, useMemo, useEffect, useRef, useCallback, startTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Network,
  Plus,
  Box,
  ChevronRight,
  ChevronDown,
  Grid3x3,
  Trash2,
  CheckCircle2,
  CheckCircle,
  AlertCircle,
  UserCheck,
  X,
  Loader2,
  Check,
  Cable,
  Search,
  Sun,
  Maximize2,
  Layers,
  Package,
  SlidersHorizontal,
  UserX,
  Users
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { ObjectId } from 'bson';
import { equipmentService } from '../../../../services/equipmentService';
import { assetService } from '../../../../services/assetService';
import { Asset } from '../../../../types/asset';
import projectAssetService, { ProjectAsset } from '../../../../services/projectAssetService';
import manufacturerService from '../../../../services/manufacturerService';
import makeAndModelService from '../../../../services/makeAndModelService';
import RightDrawer from '../../../../shared/components/form/RightDrawer';
import { mapToBackendAssetType } from '../../utils/assetTypeMapper';
import { getRecommendedChildren, StandardChildConfig } from '../solarStandardConfigs';
import BulkEquipmentDrawer from '../BulkEquipmentDrawer';
import EquipmentAssignmentDrawer from '../EquipmentAssignmentDrawer';
import ProjectAssetCheckpointDrawer from '../ProjectAssetCheckpointDrawer';
import EquipmentEditDrawer from '../EquipmentEditDrawer';
import ConfirmationDrawer from '../../../../components/common/ConfirmationDrawer';
import ManufacturerForm from '../../../manufacturer/components/ManufacturerForm';
import MakeAndModelForm from '../../../manufacturer/components/MakeAndModelForm';
import { getProjectApprovalDetails, ProjectApprovalDetails, ResourceApprovalDetail } from '../../../../services/projectApprovalService';
import { getProjectPendingChanges } from '../../../../services/postStartChangeService';

import type { ProjectAssetTreeViewProps, VisualEquipment } from './types';
import {
  LAYOUT,
  TREE_GRID,
  EQUIPMENT_CARD_BG,
  EQUIPMENT_CARD_BORDER,
  EQUIPMENT_CARD_RING_FOCUS,
  TREE_PERF,
  DC_LAYOUT_PANEL,
  getDcLayoutStringBoxGrid
} from './constants';
import {
  getAssetTypeConfig,
  getEquipmentTypeDisplayLabel,
  isCombinerBoxAssetType,
  isGenerationAsset,
  isInverterEquipmentType,
  isSolarDcLayoutEquipmentType,
  isStringAsset,
  isStringGroupingProjectAssetName
} from './assetTypeConfig';
import { calculateTreeLayout, calculateGridDimensions } from './treeLayout';
import TreeConnections from './components/TreeConnections';
import TreeGroupBorders from './components/TreeGroupBorders';
import { getPlacementMismatchMessage } from './solarArchitectureRules';
import { ZoomControls } from './components/ZoomControls';
import { TreeLoadingState, TreeErrorState } from './components/TreeLoadingStates';
import { normalizeEquipmentId } from './idUtils';
import {
  applyVirtualAssignmentInheritance,
  buildChildrenByParentId,
  buildResourceDisplayByNodeId
} from './utils/tree-helpers';
import {
  computeSolarDcLayoutTotals,
  expandSolarLayoutWithLazyModuleVirtualization,
  expandStringModuleCountToVirtualNodes,
  getStringEquipmentModuleCount,
  hasFullStringModuleDetail,
  inverterSolarDcLayoutAggregatedForCombiners,
  inverterSolarDcLayoutFromStringModules,
  isSolarDcLayout,
  SOLAR_DC_LAYOUT_EQUIPMENT_TYPE
} from './solarPlantSizing';
import { findMasterForRole } from './solarSetupCatalog';
import { useTreeZoom } from './hooks/useTreeZoom';
import { useTreeNavigation } from './hooks/useTreeNavigation';
import {
  useEquipmentQuery,
  useAssigneesQuery,
  useProjectEquipmentSummaryQuery,
  mergeCreatedIntoEquipmentTreeCache,
  getEquipmentTreeQueryKey,
  type Equipment as TreeEquipmentRow,
  type EquipmentResponse
} from './hooks/useEquipmentQuery';
import SolarPlantSetupWizard from './components/SolarPlantSetupWizard';

const EQUIPMENT_BREAKDOWN_MAX_LABELS = 10;

/** True when a dedicated Solar DC Layout equipment row exists under this parent (layout lives on child). */
function equipmentHasSolarDcLayoutChild(
  parentId: string,
  equipmentList: Array<{
    _id?: string;
    id?: string;
    parentId?: string | null;
    parentEquipmentId?: string | null;
    assetType?: string;
    hierarchyLevel?: string;
  }>
): boolean {
  const pid = normalizeEquipmentId(parentId);
  if (!pid) return false;
  for (const eq of equipmentList) {
    const p = normalizeEquipmentId(eq.parentId ?? eq.parentEquipmentId);
    if (p !== pid) continue;
    if (isSolarDcLayoutEquipmentType(String(eq.assetType || ''), String(eq.hierarchyLevel || ''))) return true;
  }
  return false;
}

/** Compact "12× Inverter · 48× String …" for tree header (full list in `title`). */
function formatProjectEquipmentBreakdownShort(
  breakdown: Array<{ label: string; count: number }>,
  maxLabels = EQUIPMENT_BREAKDOWN_MAX_LABELS
): string {
  if (!breakdown.length) return '';
  const slice = breakdown.slice(0, maxLabels);
  const parts = slice.map((r) => `${r.count}× ${r.label}`);
  const more = breakdown.length - slice.length;
  if (more > 0) parts.push(`${more} more type${more === 1 ? '' : 's'}`);
  return parts.join(' · ');
}

/** Rejected resource rows linked to a given equipment id (assignment approval flow). */
function collectRejectedResourcesForEquipment(
  approvalDetails: ProjectApprovalDetails,
  equipmentId: string
): ResourceApprovalDetail[] {
  const rejectedResources: ResourceApprovalDetail[] = [];
  approvalDetails.resources.hierarchy.forEach((supervisor) => {
    if (supervisor.approvalStatus === 'rejected') {
      const hasEquipment = supervisor.equipment?.some((eq) => eq._id === equipmentId);
      if (hasEquipment) rejectedResources.push(supervisor);
    }
    supervisor.subordinates?.forEach((engineer) => {
      if (engineer.approvalStatus === 'rejected') {
        const hasEquipment = engineer.equipment?.some((eq) => eq._id === equipmentId);
        if (hasEquipment) rejectedResources.push(engineer);
      }
    });
  });
  approvalDetails.resources.independentEngineers.forEach((engineer) => {
    if (engineer.approvalStatus === 'rejected') {
      const hasEquipment = engineer.equipment?.some((eq) => eq._id === equipmentId);
      if (hasEquipment) rejectedResources.push(engineer);
    }
  });
  return rejectedResources;
}

/** Split S strings across C combiner boxes; counts differ by at most one */
function distributeStringsAcrossCombiners(stringCount: number, combinerBoxCount: number): number[] {
  if (combinerBoxCount <= 0 || stringCount <= 0) return [];
  const base = Math.floor(stringCount / combinerBoxCount);
  const rem = stringCount % combinerBoxCount;
  return Array.from({ length: combinerBoxCount }, (_, j) => (j < rem ? base + 1 : base));
}

/** Fetch full `solarDcLayout` (per-string counts) once per expand when the tree API only returned a slim layout. */
function equipmentNeedsSolarLayoutHydrate(equipment: VisualEquipment, willExpand: boolean): boolean {
  if (!willExpand) return false;
  const layout = equipment.solarDcLayout;
  if (!isSolarDcLayout(layout) || layout.stringsPerInverter < 1) return false;
  if (layout.combinerBoxCount > 0) return false;
  const isDc = isSolarDcLayoutEquipmentType(equipment.assetType || '');
  const isInv = isInverterEquipmentType(equipment.assetType);
  const isComb = isCombinerBoxAssetType(equipment.assetType);
  if (!isDc && !isInv && !isComb) return false;
  if (hasFullStringModuleDetail(layout)) return false;
  return true;
}

/**
 * When expanding an SCB or inverter, expand every virtual DC string id and every persisted string child
 * so modules appear together with strings (no second click per string).
 */
function collectBulkStringModuleExpansionIds(
  parent: VisualEquipment,
  rows: TreeEquipmentRow[]
): string[] {
  const pid = parent.id;
  const equipmentRow = rows.find((r) => normalizeEquipmentId(r._id ?? r.id) === pid);
  /** Prefer API row; fallback to visual row (tree cache can miss a row or omit layout briefly). */
  const layoutRaw =
    equipmentRow && isSolarDcLayout(equipmentRow.solarDcLayout)
      ? equipmentRow.solarDcLayout
      : isSolarDcLayout(parent.solarDcLayout)
        ? parent.solarDcLayout
        : undefined;
  const out: string[] = [];

  const isComb = isCombinerBoxAssetType(parent.assetType);
  const isInv = isInverterEquipmentType(parent.assetType);
  const isDcLayout = isSolarDcLayoutEquipmentType(parent.assetType || '');

  if (isSolarDcLayout(layoutRaw) && layoutRaw.combinerBoxCount === 0 && layoutRaw.stringsPerInverter >= 1) {
    if (isComb || isInv || isDcLayout) {
      for (let s = 1; s <= layoutRaw.stringsPerInverter; s++) {
        out.push(`${pid}::str-${s}`);
      }
    }
  }

  if (isInv) {
    for (const r of rows) {
      const p = normalizeEquipmentId(r.parentId ?? r.parentEquipmentId);
      if (p !== pid) continue;
      const at = String(r.assetType || r.hierarchyLevel || '');
      if (!isStringAsset(at) || isCombinerBoxAssetType(at)) continue;
      const sid = normalizeEquipmentId(r._id ?? r.id);
      if (!sid) continue;
      if (getStringEquipmentModuleCount(r as Record<string, unknown>) <= 0) continue;
      out.push(sid);
    }
  }

  return out;
}

export default function ProjectAssetTreeView({
  project,
  onEquipmentUpdate,
  onOpenCheckpoints,
  onAddMasterAsset,
  availableAssets,
  projectAssets
}: ProjectAssetTreeViewProps) {
  // React Query for equipment data with caching and retry logic
  const projectId = project?._id || project?.id;
  const {
    data: equipmentData,
    isLoading: equipmentLoading,
    isError: equipmentError,
    error: equipmentErrorDetails,
    refetch: refetchEquipment,
    loadChildrenForEquipment,
    loadingChildrenForParentId,
    hydrateEquipmentRow,
    hydratingEquipmentId
  } = useEquipmentQuery({ projectId: projectId || '' });

  const {
    data: equipmentSummary,
    isLoading: equipmentSummaryLoading,
    refetch: refetchEquipmentSummary
  } = useProjectEquipmentSummaryQuery(projectId || '');
  const persistedEquipmentTotal = equipmentSummary?.total ?? 0;
  const equipmentBreakdown = equipmentSummary?.breakdown ?? [];
  const persistedModuleTotal = equipmentSummary?.moduleCount ?? 0;

  // PV modules live as `pvModuleCount` on inverter/string equipment, not as separate
  // Equipment rows — so the backend breakdown omits them. Inject a single "PV Module"
  // entry (sorted into place) so the inline summary text mirrors the standalone pill.
  const equipmentBreakdownWithModules = useMemo(() => {
    if (persistedModuleTotal <= 0) return equipmentBreakdown;
    const alreadyHasModuleRow = equipmentBreakdown.some((b) =>
      /\b(module|spv|pv\s*array|solar\s*panel|photovoltaic)\b/i.test(b.label || '')
    );
    if (alreadyHasModuleRow) return equipmentBreakdown;
    const moduleEntry = { label: 'PV Module', count: persistedModuleTotal };
    return [...equipmentBreakdown, moduleEntry].sort(
      (a, b) => b.count - a.count || (a.label || '').localeCompare(b.label || '')
    );
  }, [equipmentBreakdown, persistedModuleTotal]);

  // React Query for assignees data
  const {
    data: assigneesData,
    isLoading: assigneesLoading,
    refetch: fetchAssignees
  } = useAssigneesQuery(projectId || '');

  // Query client for cache invalidation
  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const {
    data: pendingChangesData,
    refetch: refetchPendingChanges,
  } = useQuery({
    queryKey: ['projectPendingChanges', projectId],
    queryFn: () => getProjectPendingChanges(projectId || ''),
    enabled: !!projectId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
  const pendingChangesTotal = pendingChangesData?.data?.total ?? 0;

  // Extract equipment list from React Query data
  const equipmentList = equipmentData?.data || [];
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  /** Virtual string ids + persisted string ids: module rows are built only when present (lazy modules). */
  const [expandedVirtualStringIds, setExpandedVirtualStringIds] = useState<string[]>([]);
  const [addModalState, setAddModalState] = useState<{
    open: boolean;
    ghost?: any;
    quantity: number;
    namingPattern?: string;
    manufacturer?: string;
    makeAndModel?: string;
    makeAndModelId?: string;
    stringCount?: number;
    combinerBoxCount?: number;
    warranty?: {
      provider: string;
      termsYears: number;
      terms: string;
    };
    vendor?: {
      name: string;
    };
    notes?: string;
    documents: Array<{
      documentName: string;
      documentType: string;
      documentFile: string;
    }>;
  }>({
    open: false,
    quantity: 1,
    manufacturer: '',
    makeAndModel: '',
    makeAndModelId: '',
    warranty: { provider: '', termsYears: 0, terms: '' },
    vendor: { name: '' },
    notes: '',
    documents: []
  });

  // State for new document input in bulk creation
  const [newBulkDocument, setNewBulkDocument] = useState({
    documentName: '',
    documentType: 'Manual',
    documentFile: ''
  });

  const [solarWizardOpen, setSolarWizardOpen] = useState(false);

  const showSolarSetupGuide = useMemo(() => {
    const c = `${project.projectCategory || ''} ${project.legacyProjectCategory || ''}`.toLowerCase();
    const byCategory = c.includes('solar') || c.includes('pv') || c.includes('photovoltaic');
    const byCatalog =
      availableAssets.some(a => /inverter/i.test(a.name)) &&
      availableAssets.some(
        a => /(module|spv|pv array|solar panel)/i.test(a.name) && !/cable|meter|monitor/i.test(a.name)
      );
    return byCategory || byCatalog;
  }, [project.projectCategory, project.legacyProjectCategory, availableAssets]);

  // Assignment state
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState<Set<string>>(new Set());
  const [assignmentMode, setAssignmentMode] = useState<boolean>(false);
  const [showAssignmentDrawer, setShowAssignmentDrawer] = useState<boolean>(false);
  // Use assignees from React Query instead of local state
  const availableAssignees = assigneesData || [];

  /** Tree list API often omits `siteEngineerName`; resolve from engineers-with-assignments payload */
  const assigneeNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of availableAssignees) {
      const id = a?._id != null ? String(a._id) : '';
      if (!id) continue;
      const n = typeof a.name === 'string' ? a.name.trim() : '';
      if (n) m.set(id, n);
    }
    return m;
  }, [availableAssignees]);

  // Approval data state
  const [approvalDetails, setApprovalDetails] = useState<ProjectApprovalDetails | null>(null);

  // Equipment editing state
  const [showEquipmentEditDrawer, setShowEquipmentEditDrawer] = useState(false);
  const [currentEquipment, setCurrentEquipment] = useState<any | null>(null);
  // Selection state
  const [manufacturers, setManufacturers] = useState<any[]>([]);
  const [makeAndModels, setMakeAndModels] = useState<any[]>([]);
  const [dropdownMakeAndModels, setDropdownMakeAndModels] = useState<any[]>([]);
  const [hasRejections, setHasRejections] = useState(false);
  const [selectedAssetForCheckpoints, setSelectedAssetForCheckpoints] = useState<ProjectAsset | null>(null);
  const [showCheckpointsDrawer, setShowCheckpointsDrawer] = useState(false);

  // Master data creation state
  const [showManufacturerCreator, setShowManufacturerCreator] = useState(false);
  const [showMakeAndModelCreator, setShowMakeAndModelCreator] = useState(false);
  const [selectedManufacturerForModel, setSelectedManufacturerForModel] = useState<string>('');

  // String grouping state
  const [stringGroupingState, setStringGroupingState] = useState<{
    open: boolean;
    totalModules: number;
    parentType: string | null;
    parentId: string | null;
    suggestedStrings: Array<{ stringName: string; moduleCount: number }>;
  }>({
    open: false,
    totalModules: 0,
    parentType: null,
    parentId: null,
    suggestedStrings: []
  });

  // Master assets data - stores full asset info including code and category
  const [masterAssets, setMasterAssets] = useState<Asset[]>([]);

  // Tree view enhancements with custom hooks
  const { zoomLevel, zoomIn, zoomOut, resetZoom, canZoomIn, canZoomOut } = useTreeZoom();

  const { scrollContainerRef } = useTreeNavigation();

  // Filter panel visibility
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  /** Left column: filter project assets by name / code / description */
  const [assetSearchQuery, setAssetSearchQuery] = useState('');

  /** Assets section collapse/expand. Default: collapsed so the equipment tree gets full vertical room. */
  const [assetsExpanded, setAssetsExpanded] = useState(false);

  /** Full-screen view drawer for the Site Equipment Network. */
  const [fullViewOpen, setFullViewOpen] = useState(false);

  /** Advanced filter for the equipment tree — popover open state + active filter. */
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [equipmentFilter, setEquipmentFilter] = useState<{
    mode: 'none' | 'unassigned' | 'byResource';
    resourceId?: string;
  }>({ mode: 'none' });

  /** Catalog tiles: exclude String / DC String (module combiners only); combiner / SCB stay. */
  const projectAssetsVisibleInStrip = useMemo(
    () => (projectAssets || []).filter((a) => !isStringGroupingProjectAssetName(a.name || '')),
    [projectAssets]
  );

  const filteredProjectAssets = useMemo(() => {
    if (!projectAssetsVisibleInStrip.length) return [];
    const q = assetSearchQuery.trim().toLowerCase();
    if (!q) return projectAssetsVisibleInStrip;
    return projectAssetsVisibleInStrip.filter(a => {
      const name = (a.name || '').toLowerCase();
      const code = (a.code || '').toLowerCase();
      const desc = (a.description || '').toLowerCase();
      return name.includes(q) || code.includes(q) || desc.includes(q);
    });
  }, [projectAssetsVisibleInStrip, assetSearchQuery]);








  // Child Selection State
  const [childSelectionState, setChildSelectionState] = useState<{
    open: boolean;
    parentId: string | null;
    parentType: string | null;
    warningOpen: boolean;
    selectedAsset: any | null;
  }>({
    open: false,
    parentId: null,
    parentType: null,
    warningOpen: false,
    selectedAsset: null
  });


  // Confirmation Drawer State
  const [confirmationState, setConfirmationState] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant: 'danger' | 'warning' | 'info' | 'success';
    loading: boolean;
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => { },
    variant: 'danger',
    loading: false,
  });

  // Bulk Equipment Creation Loading State
  const [isBulkCreatingEquipment, setIsBulkCreatingEquipment] = useState(false);

  const treeContainerRef = useRef<HTMLDivElement | null>(null);

  // Fetch equipment is now handled by React Query
  // Keep a refetch function for manual refresh
  const fetchEquipment = async () => {
    await Promise.all([refetchEquipment(), refetchEquipmentSummary()]);
  };

  const fetchApprovalData = async () => {
    if (!project?._id && !project?.id) return;
    try {
      const projectId = project._id || project.id;
      const data = await getProjectApprovalDetails(projectId);
      setApprovalDetails(data);

      // Check if there are any rejections
      const hasCheckpointRejections = data.assets.some((asset: any) =>
        asset.checkpoints.projectLevelCheckpoints.some((cp: any) => cp.approvalStatus === 'rejected') ||
        asset.checkpoints.masterCheckpoints.some((cp: any) => cp.approvalStatus === 'rejected')
      );

      const hasResourceRejections =
        data.resources.hierarchy.some((r: any) => r.approvalStatus === 'rejected') ||
        data.resources.independentEngineers.some((r: any) => r.approvalStatus === 'rejected');

      setHasRejections(hasCheckpointRejections || hasResourceRejections);
    } catch (error) {
      console.error('Failed to fetch approval data:', error);
      // Don't show error toast as this is optional data
    }
  };

  useEffect(() => {
    // Equipment and assignees are auto-fetched by React Query
    // Only fetch approval data manually
    fetchApprovalData();
  }, [project]);

  // Load master data for equipment editing
  useEffect(() => {
    const loadMasterData = async () => {
      try {
        const [mfgData, mmData, assetsData] = await Promise.all([
          manufacturerService.manufacturerService.getManufacturers({ limit: 1000 }),
          makeAndModelService.getMakeAndModels({ limit: 1000 }),
          assetService.getAllAssets()
        ]);
        setManufacturers(mfgData.data || []);
        setMakeAndModels(mmData.data || []);
        setMasterAssets(assetsData || []);
      } catch (error) {
        console.error('Failed to load master data:', error);
      }
    };
    loadMasterData();
  }, []);

  // Fetch make and models dynamically when manufacturer changes in add modal
  useEffect(() => {
    const fetchModels = async () => {
      if (addModalState.open && addModalState.manufacturer) {
        try {
          const models = await makeAndModelService.getMakeAndModelsForDropdown(addModalState.manufacturer);
          setDropdownMakeAndModels(models || []);
        } catch (error) {
          console.error('Failed to fetch make and models:', error);
          setDropdownMakeAndModels([]);
        }
      } else {
        setDropdownMakeAndModels([]);
      }
    };

    fetchModels();
  }, [addModalState.manufacturer, addModalState.open]);

  const handleAddBulkDocument = () => {
    if (!newBulkDocument.documentName || !newBulkDocument.documentFile) {
      toast.error('Please fill in document name and file URL');
      return;
    }
    setAddModalState(prev => ({
      ...prev,
      documents: [...prev.documents, { ...newBulkDocument }]
    }));
    setNewBulkDocument({ documentName: '', documentType: 'Manual', documentFile: '' });
  };

  const handleRemoveBulkDocument = (index: number) => {
    setAddModalState(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  // Handlers for manufacturer and make/model creation
  const handleManufacturerCreated = async (newManufacturer: any) => {
    try {
      // Refresh manufacturers list
      const mfgData = await manufacturerService.manufacturerService.getManufacturers({ limit: 1000 });
      setManufacturers(mfgData.data || []);

      // Auto-select the newly created manufacturer
      const manufacturerId = newManufacturer._id || newManufacturer.id;
      setAddModalState({
        ...addModalState,
        manufacturer: manufacturerId
      });

      setShowManufacturerCreator(false);
      toast.success(`Manufacturer "${newManufacturer.name}" created successfully`);
    } catch (error) {
      console.error('Failed to refresh manufacturers:', error);
      toast.error('Manufacturer created but failed to refresh list');
      setShowManufacturerCreator(false);
    }
  };

  const handleMakeAndModelCreated = async (newModel: any) => {
    try {
      // Refresh make & models list for current manufacturer
      if (addModalState.manufacturer) {
        const models = await makeAndModelService.getMakeAndModelsForDropdown(addModalState.manufacturer);
        setDropdownMakeAndModels(models || []);
      }

      // Auto-select the newly created model
      const modelId = newModel._id || newModel.id;
      setAddModalState({
        ...addModalState,
        makeAndModelId: modelId,
        makeAndModel: newModel.name
      });

      setShowMakeAndModelCreator(false);
      toast.success(`Make & Model "${newModel.name}" created successfully`);
    } catch (error) {
      console.error('Failed to refresh make & models:', error);
      toast.error('Make & Model created but failed to refresh list');
      setShowMakeAndModelCreator(false);
    }
  };

  /** Alias for assignee / project-asset lookups */
  const normalizeId = normalizeEquipmentId;

  const visualEquipmentList = useMemo<VisualEquipment[]>(() => {
    const expandedVirtualSet = new Set(expandedVirtualStringIds);
    const childIdsByParentId = new Map<string, string[]>();
    for (const eq of equipmentList) {
      const selfId = normalizeEquipmentId(eq._id ?? eq.id);
      const pId = normalizeEquipmentId(eq.parentId ?? eq.parentEquipmentId);
      if (!selfId) continue;
      if (pId) {
        let arr = childIdsByParentId.get(pId);
        if (!arr) {
          arr = [];
          childIdsByParentId.set(pId, arr);
        }
        arr.push(selfId);
      }
    }

    const paByNormId = new Map<string, (typeof projectAssets)[0]>();
    for (const pa of projectAssets) {
      const id = normalizeEquipmentId(pa._id);
      if (id) paByNormId.set(id, pa);
    }

    const maById = new Map<string, (typeof masterAssets)[0]>();
    for (const ma of masterAssets) {
      if (ma._id != null) maById.set(String(ma._id), ma);
      if (ma.id != null) maById.set(String(ma.id), ma);
    }

    const parentIdsWithStringChild = new Set<string>();
    const parentIdsWithGenChild = new Set<string>();
    for (const c of equipmentList) {
      const pid = normalizeEquipmentId(c.parentId ?? c.parentEquipmentId);
      if (!pid) continue;
      const at = String(c.assetType || c.hierarchyLevel || '');
      if (isStringAsset(at)) parentIdsWithStringChild.add(pid);
      if (isGenerationAsset(at)) parentIdsWithGenChild.add(pid);
    }

    const mapOne = (eq: (typeof equipmentList)[0]): VisualEquipment | null => {
      const nameFromRow = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
      const engineerName = eq.siteEngineerName;
      const supervisorName = eq.siteSupervisorName;
      const engId = eq.siteEngineerId != null ? String(eq.siteEngineerId) : '';
      const supId = eq.siteSupervisorId != null ? String(eq.siteSupervisorId) : '';
      const resolvedEngineerName =
        nameFromRow(engineerName) || (engId ? assigneeNameById.get(engId) : undefined) || '';
      const resolvedSupervisorName =
        nameFromRow(supervisorName) || (supId ? assigneeNameById.get(supId) : undefined) || '';

      const eqPaId = normalizeEquipmentId(eq.projectAssetId);
      const eqAssetId = normalizeEquipmentId(eq.assetId);
      const projectAsset =
        (eqPaId ? paByNormId.get(eqPaId) : undefined) ??
        (eqAssetId ? paByNormId.get(eqAssetId) : undefined);
      let assetCategory: string | undefined;
      let masterAssetIdFromProject: string | undefined;
      let projectAssetName: string | undefined;
      if (projectAsset) {
        masterAssetIdFromProject = projectAsset.masterAssetId;
        projectAssetName = projectAsset.name;
        const mid = projectAsset.masterAssetId;
        const masterAsset = mid != null ? maById.get(String(mid)) : undefined;
        assetCategory = masterAsset?.architectureRules?.category;
      }
      const apiProjectAssetName =
        typeof (eq as { projectAssetName?: string }).projectAssetName === 'string'
          ? (eq as { projectAssetName?: string }).projectAssetName
          : undefined;
      projectAssetName = projectAssetName ?? apiProjectAssetName;

      const selfId = normalizeEquipmentId(eq._id ?? eq.id);
      if (!selfId) {
        return null;
      }
      const parentKey = normalizeEquipmentId(eq.parentId ?? eq.parentEquipmentId);

      const childCountRaw = (eq as { childCount?: number }).childCount;
      const lazyChildCount = typeof childCountRaw === 'number' ? childCountRaw : undefined;
      const pvm = getStringEquipmentModuleCount(eq as Record<string, unknown>);

      return {
        id: selfId,
        equipmentCode: (eq as { equipmentId?: string }).equipmentId || undefined,
        assetType: eq.assetType || eq.hierarchyLevel || 'Other',
        parentEquipmentId: parentKey,
        solarDcLayout: isSolarDcLayout(eq.solarDcLayout) ? eq.solarDcLayout : undefined,
        childEquipmentIds: [...(childIdsByParentId.get(selfId) ?? [])],
        pvModuleCount: pvm > 0 ? pvm : undefined,
        name: eq.equipmentName || eq.name || 'Unnamed',
        masterAssetId: masterAssetIdFromProject,
        projectAssetName,
        lazyChildCount,
        // Asset category from master
        assetCategory,
        // Assignment info (names filled from row or assignees query)
        isAssigned: !!(engId || supId),
        assignedTo: engId
          ? {
              id: engId,
              name: resolvedEngineerName || 'Site engineer',
              role: 'Site Engineer'
            }
          : supId
            ? {
                id: supId,
                name: resolvedSupervisorName || 'Site supervisor',
                role: 'Site Supervisor'
              }
            : undefined,
        siteSupervisorId: eq.siteSupervisorId,
        siteSupervisorName: supervisorName
      };
    };

    const base = equipmentList.map(mapOne).filter((row): row is VisualEquipment => row !== null);

    const isInverterRow = (eq: (typeof equipmentList)[0]) =>
      isInverterEquipmentType(String(eq.assetType || ''), String(eq.hierarchyLevel || ''));

    /**
     * DC layout hosts: equipment that owns `solarDcLayout` (Solar DC Layout row, or legacy
     * inverter/SCB with embedded layout). Normally we emit **one virtual String row per string**
     * so modules and work are grouped string-wise; checkpoints and generated tasks still target the
     * single persisted equipment row (`stringIndex` on tasks). For extreme layouts (virtual node
     * cap) we keep one inline summary panel instead of thousands of nodes.
     */
    const dcLayoutHostIds = new Set<string>();
    /** When true, that host renders the compact inline DC panel (summary-only mega layout). */
    const dcLayoutInlinePanelByHostId = new Map<string, boolean>();
    const virtualByDcHost = new Map<string, VisualEquipment[]>();
    for (const eq of equipmentList) {
      const layout = eq.solarDcLayout;
      if (!isSolarDcLayout(layout)) continue;
      const at = String(eq.assetType || eq.hierarchyLevel || '');
      const selfId = normalizeEquipmentId(eq._id ?? eq.id);
      if (!selfId || parentIdsWithStringChild.has(selfId)) continue;

      const isDcLayoutRow = isSolarDcLayoutEquipmentType(at);
      const isInv = isInverterRow(eq);
      const isComb = isCombinerBoxAssetType(at);
      const isLegacyInvOrScb = (isInv || isComb) && !isDcLayoutRow;

      if (isInv && layout.combinerBoxCount > 0) continue;
      if (isLegacyInvOrScb && equipmentHasSolarDcLayoutChild(selfId, equipmentList)) continue;

      if (!isDcLayoutRow && !isLegacyInvOrScb) continue;
      if (!isDcLayoutRow && !isInv && !isComb) continue;

      dcLayoutHostIds.add(selfId);

      const name = eq.equipmentName || eq.name || 'Equipment';
      const virtualNodes = expandSolarLayoutWithLazyModuleVirtualization(selfId, name, layout, expandedVirtualSet);
      virtualByDcHost.set(selfId, virtualNodes);
      const useInlinePanel =
        virtualNodes.length === 1 && virtualNodes[0]?.isDcStructureSummary === true;
      dcLayoutInlinePanelByHostId.set(selfId, useInlinePanel);
    }

    const virtualByString = new Map<string, VisualEquipment[]>();
    for (const eq of equipmentList) {
      const moduleCount = getStringEquipmentModuleCount(eq);
      if (moduleCount <= 0) continue;
      const at = String(eq.assetType || eq.hierarchyLevel || '');
      if (!isStringAsset(at) || isCombinerBoxAssetType(at)) continue;
      const strId = normalizeEquipmentId(eq._id ?? eq.id);
      if (!strId || parentIdsWithGenChild.has(strId)) continue;
      if (!expandedVirtualSet.has(strId)) continue;
      virtualByString.set(strId, expandStringModuleCountToVirtualNodes(strId, moduleCount));
    }

    const virtualListDc = [...virtualByDcHost.values()].flat();
    const virtualStringList = [...virtualByString.values()].flat();

    const merged = base.map(v => {
      const extrasDc = virtualByDcHost.get(v.id);
      const extrasStr = virtualByString.get(v.id);
      const addDc = extrasDc?.filter(e => e.parentEquipmentId === v.id).map(e => e.id) ?? [];
      const addStr = extrasStr?.filter(e => e.parentEquipmentId === v.id).map(e => e.id) ?? [];

      // Inline panel only for mega layouts (single summary virtual). Otherwise string-wise virtual rows.
      let panelAnnot: Partial<VisualEquipment> = {};
      if (dcLayoutHostIds.has(v.id) && isSolarDcLayout(v.solarDcLayout)) {
        const totals = computeSolarDcLayoutTotals(v.solarDcLayout);
        const useInlinePanel = dcLayoutInlinePanelByHostId.get(v.id) === true;
        panelAnnot = useInlinePanel
          ? {
              isDcLayoutPanelHost: true,
              dcLayoutTotalModules: totals.totalModules,
              dcLayoutMaxModulesPerString: totals.maxModulesPerString,
              isDcLayoutVirtualStringsHost: false
            }
          : { isDcLayoutPanelHost: false, isDcLayoutVirtualStringsHost: true };
      }

      if (addDc.length === 0 && addStr.length === 0 && !panelAnnot.isDcLayoutPanelHost) return v;
      return {
        ...v,
        ...panelAnnot,
        childEquipmentIds: [...v.childEquipmentIds, ...addDc, ...addStr]
      };
    });

    const flat = [...merged, ...virtualListDc, ...virtualStringList];
    return applyVirtualAssignmentInheritance(flat);
  }, [equipmentList, projectAssets, masterAssets, expandedVirtualStringIds, assigneeNameById]);

  // Ghost nodes disabled - using only "Add Child" button (must be before children index / expand effect)
  const ghostNodes = useMemo<VisualEquipment[]>(() => [], []);

  const childrenByParentId = useMemo(
    () => buildChildrenByParentId(visualEquipmentList, ghostNodes),
    [visualEquipmentList, ghostNodes]
  );

  /** Resource name on card: direct assignee or aggregated from subtree (parents show same engineer as children) */
  const resourceDisplayByNodeId = useMemo(
    () => buildResourceDisplayByNodeId(visualEquipmentList, childrenByParentId),
    [visualEquipmentList, childrenByParentId]
  );

  /**
   * Map node id → set of assignee user ids covered by self + entire subtree.
   * Powers the advanced equipment filter so a parent is highlighted whenever the chosen resource
   * is assigned anywhere below it, and the "unassigned" filter targets fully-unallocated subtrees.
   */
  const resourceIdsByNodeId = useMemo(() => {
    const out = new Map<string, Set<string>>();
    if (!visualEquipmentList.length) return out;
    const byId = new Map(visualEquipmentList.map(e => [e.id, e]));
    const visiting = new Set<string>();
    const dfs = (id: string): Set<string> => {
      const cached = out.get(id);
      if (cached) return cached;
      if (visiting.has(id)) return new Set();
      visiting.add(id);
      const acc = new Set<string>();
      const eq = byId.get(id);
      if (eq?.isAssigned && eq.assignedTo?.id) acc.add(String(eq.assignedTo.id));
      for (const ch of childrenByParentId.get(id) ?? []) {
        for (const r of dfs(ch.id)) acc.add(r);
      }
      visiting.delete(id);
      out.set(id, acc);
      return acc;
    };
    for (const node of visualEquipmentList) dfs(node.id);
    return out;
  }, [visualEquipmentList, childrenByParentId]);

  /** Counts of equipment per resource (direct assignment only) for the filter dropdown. */
  const resourceAssignmentCounts = useMemo(() => {
    const counts = new Map<string, { name: string; count: number }>();
    for (const eq of visualEquipmentList) {
      if (eq.isAssigned && eq.assignedTo?.id) {
        const id = String(eq.assignedTo.id);
        const prev = counts.get(id);
        const name = eq.assignedTo.name || prev?.name || assigneeNameById.get(id) || 'Resource';
        counts.set(id, { name, count: (prev?.count ?? 0) + 1 });
      }
    }
    return counts;
  }, [visualEquipmentList, assigneeNameById]);

  const isFilterActive = equipmentFilter.mode !== 'none' &&
    !(equipmentFilter.mode === 'byResource' && !equipmentFilter.resourceId);

  /** True when the node passes the active filter and should be highlighted. */
  const nodeMatchesFilter = useCallback((nodeId: string): boolean => {
    if (!isFilterActive) return true;
    const ids = resourceIdsByNodeId.get(nodeId);
    if (equipmentFilter.mode === 'unassigned') {
      return !ids || ids.size === 0;
    }
    if (equipmentFilter.mode === 'byResource' && equipmentFilter.resourceId) {
      return !!ids?.has(equipmentFilter.resourceId);
    }
    return true;
  }, [isFilterActive, equipmentFilter, resourceIdsByNodeId]);

  /**
   * Full-project flat equipment list (slim fields). Drives the resource tag on collapsed parents
   * so users see assignments even before expanding/loading children.
   */
  const { data: fullProjectEquipmentRows } = useQuery({
    queryKey: ['equipment', projectId, 'resource-flat'],
    queryFn: async () => {
      if (!projectId) return [] as any[];
      const rows = await equipmentService.getByProject(projectId, { view: 'tree' });
      return Array.isArray(rows) ? rows : [];
    },
    enabled: !!projectId,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  /**
   * Resource map computed from ALL project equipment (independent of lazy expansion). A parent
   * shows its descendants' assignee even when collapsed.
   */
  const resourceDisplayByNodeIdAll = useMemo(() => {
    const list = (fullProjectEquipmentRows || []) as any[];
    if (!list.length) return new Map<string, { text: string; isInherited: boolean; allNames?: string[] }>();
    const minimalNodes: VisualEquipment[] = [];
    for (const eq of list) {
      const id = normalizeEquipmentId(eq?._id ?? eq?.id);
      if (!id) continue;
      const engId = eq?.siteEngineerId != null ? String(eq.siteEngineerId) : '';
      const supId = eq?.siteSupervisorId != null ? String(eq.siteSupervisorId) : '';
      const engName =
        (typeof eq?.siteEngineerName === 'string' ? eq.siteEngineerName.trim() : '') ||
        (engId ? assigneeNameById.get(engId) : '') ||
        '';
      const supName =
        (typeof eq?.siteSupervisorName === 'string' ? eq.siteSupervisorName.trim() : '') ||
        (supId ? assigneeNameById.get(supId) : '') ||
        '';
      const assignedTo = engId
        ? { id: engId, name: engName || 'Site engineer', role: 'Site Engineer' }
        : supId
          ? { id: supId, name: supName || 'Site supervisor', role: 'Site Supervisor' }
          : undefined;
      minimalNodes.push({
        id,
        assetType: String(eq?.assetType || eq?.hierarchyLevel || 'Other'),
        parentEquipmentId: normalizeEquipmentId(eq?.parentId ?? eq?.parentEquipmentId),
        childEquipmentIds: [],
        name: String(eq?.equipmentName || eq?.name || ''),
        isAssigned: !!(engId || supId),
        assignedTo,
      } as VisualEquipment);
    }
    const childMap = new Map<string, VisualEquipment[]>();
    for (const n of minimalNodes) {
      const pid = n.parentEquipmentId;
      if (!pid) continue;
      let arr = childMap.get(pid);
      if (!arr) {
        arr = [];
        childMap.set(pid, arr);
      }
      arr.push(n);
    }
    return buildResourceDisplayByNodeId(minimalNodes, childMap);
  }, [fullProjectEquipmentRows, assigneeNameById]);

  /** Floating tag rendered above each equipment card so users can see the assignee at a glance. */
  const renderResourceTag = (
    resourceDisplay?: { text: string; isInherited: boolean; allNames?: string[] }
  ) => {
    if (!resourceDisplay) return null;
    const tooltip = resourceDisplay.allNames?.length
      ? resourceDisplay.allNames.join(' · ')
      : resourceDisplay.isInherited
        ? `${resourceDisplay.text} (from child equipment)`
        : resourceDisplay.text;
    return (
      <div
        className={`absolute -top-2.5 z-30 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-tight shadow-sm border bg-emerald-50 border-emerald-300 text-emerald-800 pointer-events-auto ${assignmentMode ? 'left-8 max-w-[calc(100%-2.5rem)]' : 'left-2 max-w-[calc(100%-1rem)]'}`}
        title={tooltip}
      >
        <UserCheck size={10} className="opacity-90 flex-shrink-0" />
        <span className="truncate">{resourceDisplay.text}</span>
      </div>
    );
  };

  const seenEquipmentIdsForExpand = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (visualEquipmentList.length === 0) return;
    startTransition(() => {
      setExpandedNodes(prev => {
        let changed = false;
        const next = new Set(prev);
        for (const e of visualEquipmentList) {
          if (!seenEquipmentIdsForExpand.current.has(e.id)) {
            seenEquipmentIdsForExpand.current.add(e.id);
            // DC hosts with inline panel or per-string virtual rows: stay collapsed on first load.
            if (e.isDcLayoutPanelHost || e.isDcLayoutVirtualStringsHost) continue;
            const nChildren = childrenByParentId.get(e.id)?.length ?? 0;
            if (nChildren <= TREE_PERF.AUTO_EXPAND_MAX_CHILDREN) {
              next.add(e.id);
              changed = true;
            }
          }
        }
        return changed ? next : prev;
      });
    });
  }, [visualEquipmentList, childrenByParentId]);

  // ===== Assignment Helper Functions =====

  /**
   * Get all descendant equipment IDs recursively (includes virtual UI nodes under the parent).
   */
  const getAllDescendants = useCallback((equipmentId: string): string[] => {
    const equipment = visualEquipmentList.find(e => e.id === equipmentId);
    if (!equipment || equipment.childEquipmentIds.length === 0) {
      return [];
    }

    const descendants: string[] = [];
    const queue = [...equipment.childEquipmentIds];

    while (queue.length > 0) {
      const childId = queue.shift()!;
      descendants.push(childId);

      const child = visualEquipmentList.find(e => e.id === childId);
      if (child && child.childEquipmentIds.length > 0) {
        queue.push(...child.childEquipmentIds);
      }
    }

    return descendants;
  }, [visualEquipmentList]);

  /**
   * Toggle selection for assign-resource mode. Selecting a parent adds that row and every
   * descendant in the visual tree; deselecting removes the same set. Bulk assignment still uses
   * expandDescendants on the server for DB resolution.
   */
  const toggleEquipmentSelection = useCallback(
    (equipmentId: string) => {
      const idNorm = normalizeEquipmentId(equipmentId);
      if (!idNorm) return;
      setSelectedEquipmentIds((prev) => {
        const next = new Set(prev);
        const descendants = getAllDescendants(idNorm).map(d => normalizeEquipmentId(d)).filter((id): id is string => Boolean(id));
        const toToggle = [idNorm, ...descendants];
        const isRemoving = next.has(idNorm);
        if (isRemoving) {
          for (const id of toToggle) next.delete(id);
        } else {
          for (const id of toToggle) next.add(id);
        }
        return next;
      });
    },
    [getAllDescendants]
  );

  /**
   * Select all equipment
   */
  const selectAllEquipment = () => {
    const allIds = new Set(visualEquipmentList.filter(e => !e.isVirtual).map(e => e.id));
    setSelectedEquipmentIds(allIds);
  };

  /**
   * Clear all selections
   */
  const clearSelection = () => {
    setSelectedEquipmentIds(new Set());
  };

  /**
   * Check if equipment or any of its descendants are assigned
   */
  const isEquipmentOrDescendantsAssigned = (equipmentId: string): boolean => {
    const equipment = visualEquipmentList.find(e => e.id === equipmentId);

    // Check if the equipment itself is assigned
    if (equipment?.isAssigned) {
      return true;
    }

    // Check if any descendant is assigned
    const descendants = getAllDescendants(equipmentId);
    return descendants.some(descendantId => {
      const descendant = visualEquipmentList.find(e => e.id === descendantId);
      return descendant?.isAssigned;
    });
  };

  // ===== Rejection Helper Functions =====

  /**
   * Get rejection/update info for an asset.
   * - Rejected: checkpoints still rejected (need action).
   * - Updated: checkpoints that were rejected and user has changed them (show "Updated" tag).
   */
  const getAssetRejectionInfo = (assetId: string) => {
    const empty = { hasRejections: false, rejectedCount: 0, rejectedCheckpoints: [], hasUpdated: false, modifiedCount: 0 };
    if (!approvalDetails) return empty;

    const asset = approvalDetails.assets.find(a => a._id === assetId);
    if (!asset) return empty;

    const allProject = asset.checkpoints?.projectLevelCheckpoints ?? [];
    const allMaster = asset.checkpoints?.masterCheckpoints ?? [];
    const rejectedCheckpoints = [
      ...allProject.filter((cp: { approvalStatus?: string }) => cp.approvalStatus === 'rejected'),
      ...allMaster.filter((cp: { approvalStatus?: string }) => cp.approvalStatus === 'rejected')
    ];
    const modifiedCheckpoints = [
      ...allProject.filter((cp: { approvalStatus?: string }) => cp.approvalStatus === 'modified'),
      ...allMaster.filter((cp: { approvalStatus?: string }) => cp.approvalStatus === 'modified')
    ];

    return {
      hasRejections: rejectedCheckpoints.length > 0,
      rejectedCount: rejectedCheckpoints.length,
      rejectedCheckpoints,
      hasUpdated: modifiedCheckpoints.length > 0,
      modifiedCount: modifiedCheckpoints.length
    };
  };

  /**
   * Handle removing/deactivating a project asset
   */
  const handleRemoveProjectAsset = async (asset: ProjectAsset) => {
    // Equipment count is sourced from the dedicated Project Assets API
    // (`/project-assets/with-checkpoints`), not derived from `equipmentList`.
    const equipmentCount = (asset as any).equipmentCount ?? 0;

    if (equipmentCount > 0) {
      toast.error(`Cannot remove asset "${asset.name}" as it has ${equipmentCount} associated equipment.`);
      return;
    }

    // Show confirmation drawer
    setConfirmationState({
      open: true,
      title: 'Remove Project Asset',
      message: `Are you sure you want to remove "${asset.name}" from this project?\n\nThis action will deactivate the asset.`,
      variant: 'danger',
      loading: false,
      onConfirm: async () => {
        setConfirmationState(prev => ({ ...prev, loading: true }));
        try {
          await projectAssetService.updateProjectAssetStatus(asset._id, 'inactive');
          toast.success(`Asset "${asset.name}" has been removed from the project.`);

          // Refresh the project assets list by calling the parent's update handler
          if (onEquipmentUpdate) {
            onEquipmentUpdate();
          }

          // Close confirmation drawer
          setConfirmationState(prev => ({ ...prev, open: false, loading: false }));
        } catch (error: any) {
          console.error('Failed to remove project asset:', error);
          toast.error(error?.response?.data?.message || 'Failed to remove asset. Please try again.');
          setConfirmationState(prev => ({ ...prev, loading: false }));
        }
      },
    });
  };

  /**
   * Get resource rejection info for equipment
   */
  const getEquipmentResourceRejection = (equipmentId: string) => {
    if (!approvalDetails) return { isRejected: false, rejectedResources: [] };

    const equipment = equipmentList.find(eq => (eq._id || eq.id) === equipmentId);
    if (!equipment) return { isRejected: false, rejectedResources: [] };

    const rejectedResources = collectRejectedResourcesForEquipment(approvalDetails, equipmentId);

    return {
      isRejected: rejectedResources.length > 0,
      rejectedResources
    };
  };

  const hasAnyRejectedResourceAssignment = useMemo(() => {
    if (!approvalDetails || equipmentList.length === 0) return false;
    return equipmentList.some((eq) => {
      const id = String(eq._id || eq.id || '');
      if (!id) return false;
      return collectRejectedResourcesForEquipment(approvalDetails, id).length > 0;
    });
  }, [approvalDetails, equipmentList]);

  const selectedIncludesRejectedResource = useMemo(() => {
    if (!approvalDetails || selectedEquipmentIds.size === 0) return false;
    for (const id of selectedEquipmentIds) {
      const inList = equipmentList.some((eq) => String(eq._id || eq.id) === id);
      if (!inList) continue;
      if (collectRejectedResourcesForEquipment(approvalDetails, id).length > 0) return true;
    }
    return false;
  }, [approvalDetails, equipmentList, selectedEquipmentIds]);

  // Layout: dynamic placement with 20px spacing (no drag-move)
  const layoutNodes = useMemo(
    () =>
      calculateTreeLayout({
        visualEquipmentList,
        ghostNodes,
        expandedNodes
      }),
    [visualEquipmentList, ghostNodes, expandedNodes]
  );

  const { NODE_WIDTH, MODULE_WIDTH, MODULE_HEIGHT, MODULE_SPACING, MODULE_GRID_PADDING, MODULE_GRID_GAP_BG } = LAYOUT;

  const handleDeleteEquipment = async (id: string) => {
    // Check if equipment or its descendants are assigned
    const equipment = visualEquipmentList.find(e => e.id === id);

    if (!equipment) {
      toast.error("Equipment not found");
      return;
    }

    // Check if this equipment is assigned
    if (equipment.isAssigned) {
      toast.error(`Cannot delete "${equipment.name}" - it is assigned to ${equipment.assignedTo?.name}. Please unassign it first.`);
      return;
    }

    // Check if any descendant is assigned
    const descendants = getAllDescendants(id);
    const assignedDescendants = descendants
      .map(descId => visualEquipmentList.find(e => e.id === descId))
      .filter(desc => desc?.isAssigned);

    if (assignedDescendants.length > 0) {
      const descendantNames = assignedDescendants.map(d => d!.name).join(', ');
      toast.error(
        `Cannot delete "${equipment.name}" - it has assigned child equipment: ${descendantNames}. Please unassign them first.`
      );
      return;
    }

    // Show confirmation drawer
    const descendantCount = descendants.length;
    const confirmMessage = descendantCount > 0
      ? `Are you sure you want to delete "${equipment.name}" and its ${descendantCount} child equipment?\n\nThis action cannot be undone.`
      : `Are you sure you want to delete "${equipment.name}"?\n\nThis action cannot be undone.`;

    setConfirmationState({
      open: true,
      title: 'Delete Equipment',
      message: confirmMessage,
      variant: 'danger',
      loading: false,
      onConfirm: async () => {
        setConfirmationState(prev => ({ ...prev, loading: true }));
        try {
          await equipmentService.deleteEquipment(id);
          toast.success("Equipment deleted successfully");
          // Invalidate queries to force fresh data fetch
          await queryClient.invalidateQueries({ queryKey: ['equipment', projectId] });
          fetchAssignees(); // Refresh assignees as well
          if (onEquipmentUpdate) onEquipmentUpdate();

          // Close equipment details drawer if this item was open there
          setCurrentEquipment(prev => {
            if (prev && String(prev._id || prev.id) === id) {
              setShowEquipmentEditDrawer(false);
              return null;
            }
            return prev;
          });

          // Close confirmation drawer
          setConfirmationState(prev => ({ ...prev, open: false, loading: false }));
        } catch (error) {
          console.error("Delete failed", error);
          toast.error("Failed to delete equipment");
          setConfirmationState(prev => ({ ...prev, loading: false }));
        }
      },
    });
  };

  // Equipment editing handlers – open right-side Equipment Details drawer
  const handleEquipmentClick = (equipment: any) => {
    if (assignmentMode) return; // Don't open edit drawer in assignment mode

    if (equipment.isVirtual && equipment.isPvSlotsSummary) {
      const pid = normalizeEquipmentId(equipment.parentEquipmentId);
      const parentEq = pid
        ? equipmentList.find((e: { _id?: string; id?: string }) => String(e._id || e.id) === pid)
        : undefined;
      if (parentEq) {
        toast.info('Module count is stored on the string equipment record.');
        setCurrentEquipment(parentEq);
        setShowEquipmentEditDrawer(true);
      }
      return;
    }

    if (equipment.isVirtual) {
      let cur: VisualEquipment | undefined = equipment;
      while (cur?.isVirtual) {
        const pid = normalizeEquipmentId(cur.parentEquipmentId);
        if (!pid) break;
        cur = visualEquipmentList.find(e => e.id === pid);
      }
      const anchorId = cur?.id;
      if (anchorId) {
        const parentEq = equipmentList.find((e: { _id?: string; id?: string }) => String(e._id || e.id) === anchorId);
        if (parentEq) {
          const t = String(parentEq.assetType || parentEq.hierarchyLevel || '');
          if (isSolarDcLayoutEquipmentType(t)) {
            toast.info(
              'Each string is a view group under this Solar DC Layout row; layout and checkpoints are stored on this equipment record.'
            );
          } else if (isSolarDcLayout(parentEq.solarDcLayout) && isInverterEquipmentType(t)) {
            toast.info(
              'Each string is a view group under this inverter; layout and checkpoints are stored on this equipment record.'
            );
          } else if (isStringAsset(t)) {
            toast.info('Module count is stored on this string record.');
          }
          setCurrentEquipment(parentEq);
          setShowEquipmentEditDrawer(true);
        }
      }
      return;
    }

    const id = equipment.id || equipment._id;
    const vw = visualEquipmentList.find(e => e.id === id);
    if (vw) {
      const placementHint = getPlacementMismatchMessage(vw, visualEquipmentList);
      if (placementHint) {
        toast.warning(placementHint);
      }
    }

    // Use full equipment from list when available so drawer has all fields immediately
    const fromList = id ? equipmentList.find((e: any) => (e._id || e.id) === id) : undefined;
    const initialEquipment = fromList || equipment;
    setCurrentEquipment(initialEquipment);
    setShowEquipmentEditDrawer(true);
  };

  const handleEquipmentEditSuccess = async () => {
    // Invalidate equipment query cache to force fresh data fetch
    await queryClient.invalidateQueries({ queryKey: ['equipment', projectId] });
    if (onEquipmentUpdate) onEquipmentUpdate();
  };

  const openAddModal = (ghost: any) => {
    setAddModalState({
      open: true,
      ghost,
      quantity: ghost.config?.unitsPerParent || 1,
      namingPattern: ghost.config?.namingPattern || (ghost.assetType === 'String' ? 'STR-{seq}' : '{asset}-{seq}'),
      documents: []
    });
  };

  /** Match project asset by name for DC Cable / AC Cable bulk-add-as-child */
  const findCableProjectAsset = (kind: 'dc' | 'ac') =>
    (projectAssets || []).find(pa => {
      const n = (pa.name || '').toLowerCase();
      if (kind === 'dc') return /\bdc\b/.test(n) && n.includes('cable');
      return /\bac\b/.test(n) && n.includes('cable');
    });

  /** True if this equipment already has a direct child that matches DC/AC cable naming (same rules as project-asset cable lookup). */
  const equipmentHasCableChild = (parent: VisualEquipment, kind: 'dc' | 'ac', allEquipment: VisualEquipment[]): boolean => {
    for (const cid of parent.childEquipmentIds || []) {
      const ch = allEquipment.find(e => e.id === cid);
      if (!ch || ch.isGhost) continue;
      const blob = `${ch.assetType || ''} ${ch.name || ''}`.toLowerCase();
      if (!blob.includes('cable')) continue;
      if (kind === 'dc' && /\bdc\b/.test(blob)) return true;
      if (kind === 'ac' && /\bac\b/.test(blob)) return true;
    }
    return false;
  };

  const openCableChildModal = (parentEquipment: VisualEquipment, kind: 'dc' | 'ac') => {
    const pa = findCableProjectAsset(kind);
    if (!pa) {
      toast.error(
        kind === 'dc'
          ? 'Add a DC Cable asset to this project first (Project Assets), then use +DC.'
          : 'Add an AC Cable asset to this project first (Project Assets), then use +AC.'
      );
      return;
    }
    openAddModal({
      id: `ghost-cable-${parentEquipment.id}-${kind}`,
      assetName: pa.name,
      assetType: pa.name,
      masterAssetId: pa.masterAssetId,
      projectAssetId: pa._id,
      parentId: parentEquipment.id,
      parentType: parentEquipment.assetType,
      config: { unitsPerParent: 1, namingPattern: '{asset}-{seq}' }
    });
  };

  // Calculate optimal string distribution for modules
  const calculateStringDistribution = (totalModules: number): Array<{ stringName: string; moduleCount: number }> => {
    const IDEAL_MODULES_PER_STRING = 28;
    const MIN_MODULES_PER_STRING = 24;
    const MAX_MODULES_PER_STRING = 30;

    let modulesPerString = IDEAL_MODULES_PER_STRING;
    let stringCount = Math.ceil(totalModules / modulesPerString);

    // Adjust if distribution is uneven
    if (totalModules % modulesPerString < MIN_MODULES_PER_STRING && totalModules % modulesPerString > 0) {
      // Redistribute to avoid small remainders
      stringCount = Math.floor(totalModules / MIN_MODULES_PER_STRING);
      if (stringCount === 0) stringCount = 1;
      modulesPerString = Math.ceil(totalModules / stringCount);
    }

    const suggestions = [];
    let remaining = totalModules;

    for (let i = 1; i <= stringCount; i++) {
      const count = Math.min(modulesPerString, remaining);
      suggestions.push({
        stringName: `String ${i}`,
        moduleCount: count
      });
      remaining -= count;
    }

    return suggestions;
  };

  const handleBulkCreate = async () => {
    if (!addModalState.ghost) return;
    
    setIsBulkCreatingEquipment(true);
    try {
      const { ghost, quantity } = addModalState;
      const projectId = project._id || project.id;

      // Use projectAssetId from ghost if available, otherwise find/create it
      let projectAsset;
      if (ghost.projectAssetId) {
        // Direct projectAssetId provided (from empty state asset cards)
        projectAsset = projectAssets.find(pa => pa._id === ghost.projectAssetId);
        if (!projectAsset) {
          throw new Error('Project asset not found');
        }
      } else {
        // Find by masterAssetId (from ghost nodes)
        projectAsset = projectAssets.find(pa => pa.masterAssetId === ghost.masterAssetId);
        if (!projectAsset) {
          projectAsset = await projectAssetService.createOrGetProjectAsset(projectId, ghost.masterAssetId);
        }
      }

      // Validate projectAsset before proceeding
      if (!projectAsset) {
        console.error('Project asset not found:', { ghost, projectAssets });
        toast.error('Project asset not found. Please add the asset to your project first.');
        return;
      }

      if (!projectAsset._id) {
        console.error('Project asset missing _id:', projectAsset);
        toast.error('Invalid project asset. Please try again.');
        return;
      }

      console.log('Creating equipment with projectAsset:', projectAsset);

      // Check if manual string grouping is specified
      if (addModalState.stringCount && addModalState.stringCount > 0) {
        if (
          quantity > 28 &&
          addModalState.combinerBoxCount != null &&
          addModalState.combinerBoxCount > 0
        ) {
          await handleManualStringGroupingWithCombiners();
        } else {
          await handleManualStringGrouping();
        }
        return;
      }

      // Check if string grouping suggestion is needed (when PV modules > 10 under inverter)
      const isSolarModule = ghost.assetName?.toLowerCase().includes('module') ||
        ghost.assetName?.toLowerCase().includes('solar') ||
        ghost.assetName?.toLowerCase().includes('panel');
      const parentEquipment = ghost.parentId ? equipmentList.find(e => (e._id || e.id) === ghost.parentId) : null;
      const parentType = parentEquipment?.assetType || ghost.parentType;
      const isInverterParent = parentType && (parentType.toLowerCase().includes('inverter'));

      const shouldSuggestStrings = (
        quantity >= 10 &&
        isSolarModule &&
        isInverterParent
      );

      if (shouldSuggestStrings) {
        // Show string grouping modal
        setStringGroupingState({
          open: true,
          totalModules: quantity,
          parentType: parentType || 'Inverter',
          parentId: ghost.parentId,
          suggestedStrings: calculateStringDistribution(quantity)
        });
        return; // Pause bulk creation until user decides
      }

      const siblings = equipmentList.filter(eq =>
        (eq.parentId === ghost.parentId) && (eq.assetType === ghost.assetType)
      );

      let maxSeq = 0;
      siblings.forEach(s => {
        const match = (s.equipmentName || s.name).match(/-(\d+)$/);
        if (match) {
          const num = parseInt(match[1]);
          if (num > maxSeq) maxSeq = num;
        }
      });

      // Get naming prefix from master asset code
      // Find the master asset by matching masterAssetId
      const masterAsset = masterAssets.find(a =>
        (a._id === ghost.masterAssetId) || (a.id === ghost.masterAssetId)
      );

      // Use code from project asset if available, then master asset, otherwise fallback
      let typePrefix: string;

      // Check project asset code first (if available)
      if ((projectAsset as any).code) {
        const codeParts = (projectAsset as any).code.split('-');
        typePrefix = codeParts[0].toUpperCase();
      } else if (masterAsset?.code) {
        // Extract prefix from master asset code (e.g., "MOD-001" -> "MOD", "INV" -> "INV")
        const codeParts = masterAsset.code.split('-');
        typePrefix = codeParts[0].toUpperCase();
      } else {
        // Fallback: use 'INV' for String Inverters, otherwise first 3 letters
        const lowerName = ghost.assetName.toLowerCase();
        if (lowerName.includes('invert')) {
          typePrefix = 'INV';
        } else {
          typePrefix = ghost.assetName.substring(0, 3).toUpperCase();
        }
      }

      const itemsToCreate = [];
      for (let i = 1; i <= quantity; i++) {
        const sequence = maxSeq + i;
        const equipmentName = `${typePrefix}-${sequence}`;

        // Base item with required fields
        const item: any = {
          equipmentName,
          assetId: projectAsset._id,
          assetType: mapToBackendAssetType(ghost.assetType),
          hierarchyLevel: mapToBackendAssetType(ghost.assetType),
          parentId: ghost.parentId || null,
          projectId,
          projectAssetId: projectAsset._id,
          status: 'active',
          statusBadge: 'active'
        };

        // Add optional fields only if they have values to avoid validation errors
        if (addModalState.manufacturer) {
          item.manufacturer = addModalState.manufacturer;
          item.manufacturerId = addModalState.manufacturer;
        }

        if (addModalState.makeAndModel) {
          item.makeAndModel = addModalState.makeAndModel;
        }

        if (addModalState.makeAndModelId) {
          item.makeAndModelId = addModalState.makeAndModelId;
        }

        // Check if warranty data exists
        if (addModalState.warranty && (addModalState.warranty.provider || addModalState.warranty.terms || addModalState.warranty.termsYears > 0)) {
          item.warranty = {
            provider: addModalState.warranty.provider || '',
            terms: addModalState.warranty.terms || '',
            termsYears: addModalState.warranty.termsYears || 0
          };
        }

        // Check if vendor data exists
        if (addModalState.vendor && addModalState.vendor.name) {
          item.vendor = {
            name: addModalState.vendor.name
          };
        }

        if (addModalState.notes) {
          item.notes = addModalState.notes;
        }

        if (addModalState.documents && addModalState.documents.length > 0) {
          item.attachedDocuments = addModalState.documents;
        }

        itemsToCreate.push(item);
      }

      console.log('Items to create:', itemsToCreate);

      const bulkRes = await equipmentService.createBulkEquipment({ projectId, items: itemsToCreate });
      const createdRows = (bulkRes.data?.created ?? []) as TreeEquipmentRow[];
      if (createdRows.length > 0) {
        mergeCreatedIntoEquipmentTreeCache(queryClient, projectId, createdRows);
      }

      toast.success(`Added ${quantity} ${ghost.assetName}(s)`);

      const placementProbe: VisualEquipment = {
        id: '__placement__',
        assetType: ghost.assetType || ghost.assetName || 'Other',
        parentEquipmentId: ghost.parentId || null,
        childEquipmentIds: [],
        name: ghost.assetName || ghost.assetType || ''
      };
      const placementHint = getPlacementMismatchMessage(placementProbe, visualEquipmentList);
      if (placementHint) {
        toast.warning(placementHint);
      }

      setAddModalState({
        ...addModalState,
        open: false,
        stringCount: undefined,
        combinerBoxCount: undefined
      });

      if (onEquipmentUpdate) onEquipmentUpdate();

    } catch (e: any) {
      console.error(e);
      toast.error(e.response?.data?.message || "Failed to add assets");
    } finally {
      setIsBulkCreatingEquipment(false);
    }
  };

  /** Inverter → Combiner Box(es) → String(s) → Module(s); quantity > 28 */
  const handleManualStringGroupingWithCombiners = async () => {
    if (!addModalState.ghost || !addModalState.stringCount) return;

    try {
      const { ghost, quantity, stringCount } = addModalState;
      const combinerBoxCount = addModalState.combinerBoxCount ?? 0;
      if (combinerBoxCount < 1) {
        toast.error('Enter at least 1 string combiner box.');
        return;
      }
      if (stringCount < combinerBoxCount) {
        toast.error('Number of strings must be at least the number of combiner boxes.');
        return;
      }

      const projectId = project._id || project.id;
      const modulesPerString = Math.floor(quantity / stringCount);
      const remainingModules = quantity % stringCount;

      const stringsPerCombiner = distributeStringsAcrossCombiners(stringCount, combinerBoxCount);

      const combinerMaster = masterAssets.find(
        a => /combiner/i.test(a.name || '') && !/inverter/i.test(a.name || '')
      );
      const combinerMasterAssetId = combinerMaster?._id || combinerMaster?.id;
      if (!combinerMasterAssetId) {
        toast.error(
          'No Combiner Box master asset found. Add a Combiner Box asset in master data, then try again.'
        );
        return;
      }

      const combinerProjectAsset = await projectAssetService.createOrGetProjectAsset(
        projectId,
        combinerMasterAssetId
      );
      if (!combinerProjectAsset?._id) {
        toast.error('Failed to resolve Combiner Box project asset.');
        return;
      }

      const pvMaster = findMasterForRole(availableAssets, 'pvModule');
      if (!pvMaster) {
        toast.error('Missing PV Module master asset. Add it to the project type and project.');
        return;
      }
      const pvProjectAsset = await projectAssetService.createOrGetProjectAsset(projectId, pvMaster.masterAssetId);
      if (!pvProjectAsset?._id) {
        toast.error('Failed to resolve PV Module project asset.');
        return;
      }

      const existingCombiners = equipmentList.filter(
        eq =>
          eq.parentId === ghost.parentId &&
          /combiner/i.test(String(eq.assetType || eq.hierarchyLevel || ''))
      );
      let maxCombSeq = 0;
      existingCombiners.forEach(c => {
        const match = (c.equipmentName || c.name).match(/-(\d+)$/);
        if (match) {
          const num = parseInt(match[1], 10);
          if (num > maxCombSeq) maxCombSeq = num;
        }
      });

      const stringModuleCounts: number[] = [];
      let stringIndex = 1;
      for (let combIdx = 0; combIdx < combinerBoxCount; combIdx++) {
        const nStringsHere = stringsPerCombiner[combIdx];
        for (let k = 0; k < nStringsHere; k++) {
          const thisStringModuleCount =
            stringIndex <= remainingModules ? modulesPerString + 1 : modulesPerString;
          stringModuleCounts.push(thisStringModuleCount);
          stringIndex++;
        }
      }

      const evenStringsPerCombiner =
        stringsPerCombiner.length > 0 && stringsPerCombiner.every((n) => n === stringsPerCombiner[0])
          ? stringsPerCombiner[0]
          : undefined;

      const itemsToCreate: Record<string, unknown>[] = [];
      let sliceOffset = 0;
      for (let c = 1; c <= combinerBoxCount; c++) {
        const combSeq = maxCombSeq + c;
        const finalCombName = `SCB-${combSeq}`;
        const cid = new ObjectId().toString();
        const combIdx = c - 1;
        const nHere = stringsPerCombiner[combIdx] ?? 0;
        const slice = stringModuleCounts.slice(sliceOffset, sliceOffset + nHere);
        sliceOffset += nHere;
        const scbLayout =
          slice.length > 0
            ? inverterSolarDcLayoutFromStringModules(slice)
            : inverterSolarDcLayoutFromStringModules([Math.max(1, modulesPerString)]);
        const dcLayId = new ObjectId().toString();
        itemsToCreate.push({
          _id: cid,
          projectId,
          equipmentName: finalCombName,
          parentId: ghost.parentId ?? null,
          assetId: combinerProjectAsset._id,
          assetType: 'Combiner Box',
          hierarchyLevel: 'Combiner Box',
          status: 'active',
          statusBadge: 'active',
          ...(addModalState.manufacturer && { manufacturerId: addModalState.manufacturer }),
          ...(addModalState.makeAndModelId && { makeAndModelId: addModalState.makeAndModelId })
        });
        itemsToCreate.push({
          _id: dcLayId,
          projectId,
          equipmentName: `${finalCombName} · DC`,
          parentId: cid,
          assetId: pvProjectAsset._id,
          assetType: SOLAR_DC_LAYOUT_EQUIPMENT_TYPE,
          hierarchyLevel: SOLAR_DC_LAYOUT_EQUIPMENT_TYPE,
          status: 'active',
          statusBadge: 'active',
          solarDcLayout: scbLayout,
          zone: 'Z01'
        });
      }

      const bulkRes = await equipmentService.createBulkEquipment({ projectId, items: itemsToCreate });
      const createdRows = (bulkRes.data?.created ?? []) as TreeEquipmentRow[];
      const failedCount = bulkRes.data?.totalFailed ?? 0;
      if (createdRows.length > 0) {
        mergeCreatedIntoEquipmentTreeCache(queryClient, projectId, createdRows);
      }

      const invParentId = ghost.parentId;
      if (!invParentId) {
        toast.error('Missing parent inverter.');
        return;
      }

      if (failedCount > 0) {
        toast.warning(
          `${bulkRes.data?.totalCreated ?? createdRows.length} combiner(s) created, ${failedCount} failed. Inverter DC layout was not updated — fix errors and retry.`
        );
      } else {
        const invLayout = inverterSolarDcLayoutAggregatedForCombiners({
          totalStrings: stringCount,
          modulesPerStringUniform: Math.max(1, ...stringModuleCounts),
          combinerBoxCount,
          combinerStringDistribution: stringsPerCombiner,
          ...(evenStringsPerCombiner != null ? { evenStringsPerCombiner } : {})
        });
        await equipmentService.updateEquipment(invParentId, { solarDcLayout: invLayout });
        const totalMods = stringModuleCounts.reduce((a, b) => a + b, 0);
        toast.success(
          `Created ${combinerBoxCount} SCB(s) with Solar DC Layout under each. Inverter summary: ${stringCount} strings, ${totalMods} modules total.`
        );
      }

      await queryClient.invalidateQueries({ queryKey: ['equipment', projectId] });
      if (onEquipmentUpdate) onEquipmentUpdate();

      setAddModalState({
        ...addModalState,
        open: false,
        stringCount: undefined,
        combinerBoxCount: undefined
      });
    } catch (error: any) {
      console.error('String combiner grouping error:', error);
      toast.error(
        error.response?.data?.message || error.message || 'Failed to create combiners and strings'
      );
    }
  };

  const handleManualStringGrouping = async () => {
    if (!addModalState.ghost || !addModalState.stringCount) return;

    try {
      const { ghost, quantity, stringCount } = addModalState;
      const projectId = project._id || project.id;

      const modulesPerString = Math.floor(quantity / stringCount);
      const remainingModules = quantity % stringCount;

      const invParentId = ghost.parentId;
      if (!invParentId) {
        toast.error('Missing parent inverter.');
        return;
      }

      const stringModuleCounts: number[] = [];
      for (let i = 1; i <= stringCount; i++) {
        stringModuleCounts.push(i <= remainingModules ? modulesPerString + 1 : modulesPerString);
      }

      const pvMaster = findMasterForRole(availableAssets, 'pvModule');
      if (!pvMaster) {
        toast.error('Missing PV Module master asset. Add it to the project type and project.');
        return;
      }
      const pvProjectAsset = await projectAssetService.createOrGetProjectAsset(projectId, pvMaster.masterAssetId);
      if (!pvProjectAsset?._id) {
        toast.error('Failed to resolve PV Module project asset.');
        return;
      }

      const solarDcLayout = inverterSolarDcLayoutFromStringModules(stringModuleCounts);

      const hostRow = equipmentList.find(
        (e: { _id?: string; id?: string }) => String(e._id || e.id) === invParentId
      );
      const hostName = hostRow?.equipmentName || (hostRow as { name?: string })?.name || 'INV';

      const existingDc = equipmentList.find(
        (e: {
          _id?: string;
          id?: string;
          parentId?: string | null;
          parentEquipmentId?: string | null;
          assetType?: string;
          hierarchyLevel?: string;
        }) => {
          const p = normalizeEquipmentId(e.parentId ?? e.parentEquipmentId);
          return (
            p === invParentId &&
            isSolarDcLayoutEquipmentType(String(e.assetType || ''), String(e.hierarchyLevel || ''))
          );
        }
      );

      if (existingDc) {
        await equipmentService.updateEquipment(String(existingDc._id || existingDc.id), { solarDcLayout });
      } else {
        await equipmentService.createBulkEquipment({
          projectId,
          items: [
            {
              equipmentName: `${hostName} · DC`,
              parentId: invParentId,
              assetId: pvProjectAsset._id,
              assetType: SOLAR_DC_LAYOUT_EQUIPMENT_TYPE,
              hierarchyLevel: SOLAR_DC_LAYOUT_EQUIPMENT_TYPE,
              solarDcLayout,
              zone: 'Z01',
              status: 'active',
              statusBadge: 'active'
            }
          ]
        });
      }

      const totalMods = stringModuleCounts.reduce((a, b) => a + b, 0);
      toast.success(
        `Updated Solar DC Layout: ${stringCount} strings, ${totalMods} modules (one equipment row; checkpoints apply there).`
      );

      await queryClient.invalidateQueries({ queryKey: ['equipment', projectId] });
      if (onEquipmentUpdate) onEquipmentUpdate();

      setAddModalState({
        ...addModalState,
        open: false,
        stringCount: undefined,
        combinerBoxCount: undefined
      });
    } catch (error: any) {
      console.error('Manual string grouping error:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to create strings and modules');
    }
  };

  const handleBulkCreateWithStrings = async () => {
    if (!addModalState.ghost || !stringGroupingState.open) return;

    setIsBulkCreatingEquipment(true);
    try {
      const { parentId, suggestedStrings } = stringGroupingState;
      const projectId = project._id || project.id;

      if (!parentId) {
        toast.error('Missing parent inverter.');
        return;
      }

      const stringModuleCounts = suggestedStrings.map((s) => s.moduleCount);
      const solarDcLayout = inverterSolarDcLayoutFromStringModules(stringModuleCounts);

      const pvMaster = findMasterForRole(availableAssets, 'pvModule');
      if (!pvMaster) {
        toast.error('Missing PV Module master asset. Add it to the project type and project.');
        return;
      }
      const pvProjectAsset = await projectAssetService.createOrGetProjectAsset(projectId, pvMaster.masterAssetId);
      if (!pvProjectAsset?._id) {
        toast.error('Failed to resolve PV Module project asset.');
        return;
      }

      const hostRow = equipmentList.find(
        (e: { _id?: string; id?: string }) => String(e._id || e.id) === parentId
      );
      const hostName = hostRow?.equipmentName || (hostRow as { name?: string })?.name || 'INV';

      const existingDc = equipmentList.find(
        (e: {
          _id?: string;
          id?: string;
          parentId?: string | null;
          parentEquipmentId?: string | null;
          assetType?: string;
          hierarchyLevel?: string;
        }) => {
          const p = normalizeEquipmentId(e.parentId ?? e.parentEquipmentId);
          return (
            p === parentId &&
            isSolarDcLayoutEquipmentType(String(e.assetType || ''), String(e.hierarchyLevel || ''))
          );
        }
      );

      if (existingDc) {
        await equipmentService.updateEquipment(String(existingDc._id || existingDc.id), { solarDcLayout });
      } else {
        await equipmentService.createBulkEquipment({
          projectId,
          items: [
            {
              equipmentName: `${hostName} · DC`,
              parentId,
              assetId: pvProjectAsset._id,
              assetType: SOLAR_DC_LAYOUT_EQUIPMENT_TYPE,
              hierarchyLevel: SOLAR_DC_LAYOUT_EQUIPMENT_TYPE,
              solarDcLayout,
              zone: 'Z01',
              status: 'active',
              statusBadge: 'active'
            }
          ]
        });
      }

      toast.success(
        `Updated Solar DC Layout: ${suggestedStrings.length} strings, ${stringGroupingState.totalModules} modules (one equipment row; checkpoints apply there).`
      );

      await queryClient.invalidateQueries({ queryKey: ['equipment', projectId] });
      if (onEquipmentUpdate) onEquipmentUpdate();

      // Close modals
      setStringGroupingState({ open: false, totalModules: 0, parentType: null, parentId: null, suggestedStrings: [] });
      setAddModalState({
        ...addModalState,
        open: false,
        stringCount: undefined,
        combinerBoxCount: undefined
      });

    } catch (error: any) {
      console.error('String grouping error:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to create strings and modules');
    } finally {
      setIsBulkCreatingEquipment(false);
    }
  };

  // Ensure we have enough width for all nodes plus padding (avoid spread on huge arrays)
  let maxX = 1000;
  let maxY = 800;
  for (const n of layoutNodes) {
    maxX = Math.max(maxX, n.x + n.width / 2);
    maxY = Math.max(maxY, n.y + n.height);
  }
  maxX += 200;
  maxY += 200;

  const heavyTree = layoutNodes.length > TREE_PERF.LIGHT_MOTION_NODE_THRESHOLD;

  /**
   * Inline DC Layout summary panel (mega layouts only): entire `solarDcLayout` in one card.
   * Normal layouts use per-string virtual rows instead. Checkpoints / edit / delete still target
   * the one persisted equipment record.
   */
  const renderDcLayoutPanel = (args: {
    layoutNode: typeof layoutNodes[0];
    equipment: VisualEquipment;
    x: number;
    y: number;
    width: number;
    height: number;
    isNodeSelected: boolean;
    config: ReturnType<typeof getAssetTypeConfig>;
    Icon: ReturnType<typeof getAssetTypeConfig>['icon'];
  }) => {
    const { equipment, x, y, width, height, isNodeSelected, config, Icon } = args;
    const layout = equipment.solarDcLayout;
    if (!layout || !isSolarDcLayout(layout)) return null;

    const isExpanded = expandedNodes.has(equipment.id);
    const totals = computeSolarDcLayoutTotals(layout);
    const rowCap = DC_LAYOUT_PANEL.MAX_STRING_ROWS_RENDER;
    const cellCap = DC_LAYOUT_PANEL.MAX_CELLS_PER_ROW_RENDER;
    const modGap = DC_LAYOUT_PANEL.MODULE_CELL_GAP;
    const modW = DC_LAYOUT_PANEL.MODULE_CELL_WIDTH;
    const modH = DC_LAYOUT_PANEL.MODULE_CELL_HEIGHT;
    const rowPad = DC_LAYOUT_PANEL.ROW_PADDING;
    const visibleStrings = Math.min(totals.totalStrings, rowCap);
    const truncatedStringsCount = Math.max(0, totals.totalStrings - visibleStrings);
    const directAssigneeFallback =
      equipment.isAssigned && equipment.assignedTo?.name
        ? { text: equipment.assignedTo.name, isInherited: false }
        : undefined;
    const resourceDisplay =
      resourceDisplayByNodeIdAll.get(equipment.id) ??
      resourceDisplayByNodeId.get(equipment.id) ??
      directAssigneeFallback;
    const rejection = getEquipmentResourceRejection(equipment.id);
    const isAssignedLocked = isEquipmentOrDescendantsAssigned(equipment.id);
    const scbContext =
      layout.combinersPerInverter && layout.stringsPerCombiner
        ? `${layout.combinersPerInverter} SCB × ${layout.stringsPerCombiner} strings`
        : layout.combinerBoxCount > 0
          ? `${layout.combinerBoxCount} SCB`
          : '';

    const summaryLabel =
      `${totals.totalStrings} string${totals.totalStrings === 1 ? '' : 's'}` +
      ` · ${totals.totalModules.toLocaleString()} module${totals.totalModules === 1 ? '' : 's'}`;

    const isHydrating = hydratingEquipmentId === equipment.id;
    const togglePanel = () => {
      const willExpand = !expandedNodes.has(equipment.id);
      void (async () => {
        // Slim layouts (without per-string module counts) are hydrated once on expand so
        // the inline panel shows the accurate per-string distribution.
        if (willExpand && equipmentNeedsSolarLayoutHydrate(equipment, true)) {
          await hydrateEquipmentRow(equipment.id);
        }
        startTransition(() => {
          setExpandedNodes((prev) => {
            const next = new Set(prev);
            if (next.has(equipment.id)) next.delete(equipment.id);
            else next.add(equipment.id);
            return next;
          });
        });
      })();
    };

    const dimDc = isFilterActive && !nodeMatchesFilter(equipment.id);

    return (
      <motion.div
        key={equipment.id}
        initial={heavyTree ? false : { opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={heavyTree ? { duration: 0 } : { type: 'spring', stiffness: 300, damping: 28 }}
        className="absolute select-none group"
        style={{
          left: `${x - width / 2}px`,
          top: `${y}px`,
          width: `${width}px`,
          height: `${height}px`,
          ...(dimDc ? { opacity: 0.22, filter: 'grayscale(0.85)' } : {})
        }}
      >
        {renderResourceTag(resourceDisplay)}
        <div
          className="relative flex flex-col overflow-hidden transition-[box-shadow,border-color] duration-200 rounded-xl cursor-pointer hover:shadow-md h-full"
          style={{
            backgroundColor: EQUIPMENT_CARD_BG,
            border: `1px solid ${rejection.isRejected ? '#EF4444' : EQUIPMENT_CARD_BORDER}`,
            boxShadow: rejection.isRejected
              ? '0 2px 8px rgba(239,68,68,0.12)'
              : isNodeSelected
                ? `0 0 0 1px ${EQUIPMENT_CARD_BORDER}, 0 0 0 3px ${EQUIPMENT_CARD_RING_FOCUS}, 0 6px 16px rgba(15, 23, 42, 0.07)`
                : '0 1px 3px rgba(15, 23, 42, 0.06)'
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (assignmentMode) {
              void toggleEquipmentSelection(equipment.id);
            } else {
              setSelectedNodeId(equipment.id);
              handleEquipmentClick(equipment);
            }
          }}
        >
          {/* Assignment checkbox */}
          {assignmentMode && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -top-2 -left-2 z-20"
              onClick={(e) => {
                e.stopPropagation();
                void toggleEquipmentSelection(equipment.id);
              }}
            >
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center cursor-pointer transition-all shadow ${
                  selectedEquipmentIds.has(equipment.id)
                    ? 'bg-blue-600 border-blue-600'
                    : 'bg-white border-gray-300 hover:border-blue-400'
                }`}
              >
                {selectedEquipmentIds.has(equipment.id) && (
                  <CheckCircle2 size={14} className="text-white" />
                )}
              </motion.div>
            </motion.div>
          )}

          {/* Resource rejection badge */}
          {rejection.isRejected && !assignmentMode && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -top-1 -right-1 z-20"
            >
              <div className="flex items-center gap-0.5 px-1.5 py-0.5 bg-red-600 text-white rounded text-[10px] font-semibold tracking-tight">
                <AlertCircle size={9} />
                <span>Resource rejected</span>
              </div>
            </motion.div>
          )}

          {/* Header */}
          <div
            className="flex items-start gap-2.5 px-3 pt-2.5 pb-2 border-b border-slate-200/90 shrink-0"
            style={{ minHeight: DC_LAYOUT_PANEL.HEADER_HEIGHT }}
          >
            <div
              className="w-9 h-9 flex items-center justify-center rounded-lg border shrink-0 mt-0.5"
              style={{
                background: `linear-gradient(135deg, ${config.color}10, ${config.color}26)`,
                borderColor: `${config.color}40`
              }}
            >
              <Sun size={16} style={{ color: config.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap min-w-0">
                <h4
                  className="font-semibold text-[13px] text-slate-900 leading-snug tracking-tight truncate"
                  title={equipment.name}
                >
                  {equipment.name}
                </h4>
                {equipment.equipmentCode && (
                  <span
                    className="text-[10px] text-slate-500 font-mono tabular-nums truncate"
                    title={equipment.equipmentCode}
                  >
                    {equipment.equipmentCode}
                  </span>
                )}
              </div>
              <div className="mt-0.5 flex items-center gap-1.5 flex-wrap text-[11px]">
                <span
                  className="inline-flex items-center gap-1 rounded-full px-1.5 py-[1px] font-semibold tabular-nums"
                  style={{
                    backgroundColor: `${config.color}14`,
                    color: config.color,
                    border: `1px solid ${config.color}33`
                  }}
                  title="Strings · total modules in this Solar DC Layout equipment"
                >
                  <Grid3x3 size={10} strokeWidth={2} />
                  {summaryLabel}
                </span>
                {scbContext && (
                  <span
                    className="text-slate-500 font-medium"
                    title="Structure of strings under combiner boxes"
                  >
                    {scbContext}
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              disabled={isHydrating}
              onClick={(e) => {
                e.stopPropagation();
                togglePanel();
              }}
              className={`self-start mt-0.5 p-1.5 rounded-md shrink-0 transition-colors ${
                isExpanded
                  ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-200/90'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200/90'
              } ${isHydrating ? 'opacity-60 cursor-wait' : ''}`}
              title={isExpanded ? 'Collapse DC layout' : 'Expand DC layout'}
            >
              {isHydrating ? (
                <Loader2 size={14} strokeWidth={2.25} className="animate-spin" />
              ) : isExpanded ? (
                <ChevronDown size={14} strokeWidth={2.25} />
              ) : (
                <ChevronRight size={14} strokeWidth={2.25} />
              )}
            </button>
          </div>

          {/* Body (panel) */}
          {isExpanded && (
            <div
              className="relative flex-1 min-h-0 overflow-hidden"
              style={{
                padding: `${DC_LAYOUT_PANEL.PANEL_BODY_PADDING}px 14px`,
                backgroundImage:
                  'linear-gradient(180deg, rgba(248,250,252,0.9) 0%, rgba(241,245,249,0.9) 100%)'
              }}
            >
              <div
                className="flex flex-col"
                style={{ gap: `${DC_LAYOUT_PANEL.STRING_ROW_GAP}px` }}
              >
                {Array.from({ length: visibleStrings }, (_, idx) => {
                  const sIdx = idx + 1;
                  const nMods = totals.perStringModules[idx] ?? 0;
                  const { cellsToDraw, cols } = getDcLayoutStringBoxGrid(nMods);
                  const overflowCells = Math.max(0, nMods - cellCap);
                  const stripInnerW = cols * modW + (cols - 1) * modGap;
                  const accent = config.color;
                  return (
                    <div
                      key={sIdx}
                      className="flex items-stretch gap-2"
                      title={`String ${sIdx} · ${nMods} module${nMods === 1 ? '' : 's'}`}
                    >
                      <div
                        className="flex items-center gap-1 text-[10px] font-semibold tracking-tight text-slate-700 shrink-0 self-center"
                        style={{ width: DC_LAYOUT_PANEL.STRING_LABEL_WIDTH }}
                      >
                        <span className="inline-flex w-4 justify-center tabular-nums text-slate-500">
                          {sIdx}
                        </span>
                        <span className="truncate">STR-{sIdx}</span>
                        <span className="ml-auto text-[9px] text-slate-500 tabular-nums font-medium">
                          ×{nMods}
                        </span>
                      </div>
                      {/* String-wise framed PV strip (aluminum frame + glass field + cell tiles) */}
                      <div
                        className="relative min-w-0 flex-1 rounded-lg p-[3px] shadow-[inset_0_1px_0_rgba(255,255,255,0.55),0_2px_8px_rgba(15,23,42,0.08)]"
                        style={{
                          background:
                            'linear-gradient(145deg, #d1d9e0 0%, #eef2f6 22%, #c5ced8 55%, #aeb9c4 100%)',
                          border: '1px solid rgba(71,85,105,0.55)'
                        }}
                      >
                        <div
                          className="relative rounded-md overflow-hidden"
                          style={{
                            padding: rowPad,
                            background:
                              'linear-gradient(180deg, rgba(255,255,255,0.35) 0%, rgba(226,232,240,0.55) 18%, rgba(148,163,184,0.35) 100%)',
                            border: '1px solid rgba(51,65,85,0.35)',
                            boxShadow: 'inset 0 2px 10px rgba(15,23,42,0.12)'
                          }}
                        >
                          <div
                            className="relative flex flex-wrap items-start content-start"
                            style={{
                              width: stripInnerW,
                              maxWidth: '100%',
                              gap: modGap
                            }}
                          >
                            {cellsToDraw > 0 ? (
                              Array.from({ length: cellsToDraw }, (_, mi) => (
                                <div
                                  key={mi}
                                  className="relative shrink-0 rounded-[2px] overflow-hidden"
                                  style={{
                                    width: modW,
                                    height: modH,
                                    background: `
                                      linear-gradient(90deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0) 38%, rgba(0,0,0,0.06) 100%),
                                      linear-gradient(180deg, rgba(255,255,255,0.14) 0%, transparent 24%, transparent 76%, rgba(0,0,0,0.16) 100%),
                                      repeating-linear-gradient(90deg, rgba(0,0,0,0.07) 0 1px, transparent 1px 4px),
                                      repeating-linear-gradient(0deg, rgba(0,0,0,0.05) 0 1px, transparent 1px 5px),
                                      linear-gradient(158deg, #1e5578 0%, #0f3350 44%, #101b2c 100%)
                                    `,
                                    border: `1px solid ${accent}55`,
                                    boxShadow:
                                      'inset 0 1px 0 rgba(255,255,255,0.2), inset 0 -1px 0 rgba(0,0,0,0.22)'
                                  }}
                                  aria-hidden
                                >
                                  <div
                                    className="pointer-events-none absolute left-1/2 top-[10%] bottom-[10%] w-px -translate-x-1/2 rounded-full opacity-90"
                                    style={{
                                      background:
                                        'linear-gradient(180deg, rgba(255,255,255,0.55), rgba(255,255,255,0.1), rgba(255,255,255,0.45))'
                                    }}
                                  />
                                </div>
                              ))
                            ) : (
                              <span className="px-1 text-[9px] text-slate-600 italic">empty string</span>
                            )}
                          </div>
                          {overflowCells > 0 && (
                            <span
                              className="mt-1 block text-[9px] font-semibold text-slate-600 tabular-nums"
                              title={`${overflowCells} more module${overflowCells === 1 ? '' : 's'} not drawn for performance`}
                            >
                              +{overflowCells} modules
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {truncatedStringsCount > 0 && (
                <p className="mt-2 text-[10px] text-slate-600 leading-snug font-medium text-center">
                  +{truncatedStringsCount} more string{truncatedStringsCount === 1 ? '' : 's'}{' '}
                  omitted for performance — open equipment details to edit the full layout.
                </p>
              )}
            </div>
          )}

          {/* Footer */}
          <div
            className="flex items-center justify-between gap-1 px-3 py-2 border-t border-slate-200/90 shrink-0 bg-white"
            style={{ minHeight: DC_LAYOUT_PANEL.FOOTER_HEIGHT }}
          >
            <div className="text-[10px] text-slate-500 font-medium truncate">
              One equipment · checkpoints &amp; tasks apply to the full layout
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEquipmentClick(equipment);
                }}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 bg-white text-[10px] font-semibold text-slate-700 hover:bg-slate-50 hover:border-slate-300"
                title="Open equipment details"
              >
                <CheckCircle2 size={10} className="opacity-80" />
                Details
              </button>
              {!isAssignedLocked && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteEquipment(equipment.id);
                  }}
                  className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors shrink-0"
                  title="Delete"
                >
                  <Trash2 size={12} strokeWidth={2} />
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  const renderNode = (layoutNode: typeof layoutNodes[0]) => {
    const { equipment, x, y, width, height } = layoutNode;
    const isNodeSelected = selectedNodeId === equipment.id && !assignmentMode;
    /**
     * When the advanced filter is active, dim non-matching nodes (translucent + grayscale) and
     * leave matching nodes at full visibility. Matched nodes also get an outline accent below.
     */
    const isFilterMatch = nodeMatchesFilter(equipment.id);
    const dimNode = isFilterActive && !isFilterMatch && !equipment.isGhost;
    const filterStyle: React.CSSProperties = dimNode
      ? { opacity: 0.22, filter: 'grayscale(0.85)' }
      : {};

    if (equipment.isGhost) {
      return (
        <motion.div
          key={equipment.id}
          initial={heavyTree ? false : { opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={heavyTree ? { duration: 0 } : undefined}
          className="absolute select-none"
          style={{
            left: `${x - width / 2}px`,
            top: `${y}px`,
            width: `${width}px`
          }}
        >
        <button
          onClick={() => openAddModal(equipment)}
          className="w-full flex items-center gap-2 p-2 bg-white/95 hover:bg-slate-50 transition-all text-left group relative border border-dashed border-slate-400/85 rounded-lg shadow-sm"
        >
          <Plus size={14} className="text-slate-500 flex-shrink-0" />
          <span className="text-[11px] font-medium text-slate-700 flex-1 break-words leading-snug">
            <span className="italic text-slate-600">{equipment.name}</span>
            <span className="ml-1 font-semibold text-emerald-700 not-italic">Recommended</span>
          </span>
        </button>
        </motion.div>
      );
    }

    const config = getAssetTypeConfig(equipment.assetType, availableAssets, {
      masterAssetId: equipment.masterAssetId,
      catalogName: equipment.projectAssetName
    });
    const Icon = config.icon;
    const typeLabel = getEquipmentTypeDisplayLabel(equipment.assetType, equipment.projectAssetName);

    // ─────────────────────────────────────────────────────────────────────
    // Solar DC Layout — inline full layout panel (one equipment, combined
    // strings + modules view). Checkpoints / task generation / edit / delete
    // all target this single equipment row (logic unchanged).
    // ─────────────────────────────────────────────────────────────────────
    if (equipment.isDcLayoutPanelHost && isSolarDcLayout(equipment.solarDcLayout)) {
      return renderDcLayoutPanel({
        layoutNode,
        equipment,
        x,
        y,
        width,
        height,
        isNodeSelected,
        config,
        Icon
      });
    }

    /** Direct children — same index as tree layout */
    const directChildren = childrenByParentId.get(equipment.id) ?? [];
    const realDbChildCount = directChildren.filter(c => !c.isVirtual).length;
    const lazyN = equipment.lazyChildCount ?? 0;
    const needsLazyFetch = lazyN > realDbChildCount;
    const isCombiner = isCombinerBoxAssetType(equipment.assetType);
    /** DC String row with embedded module grid — not combiner boxes (those show strings as separate nodes below) */
    const isStringForGrid = isStringAsset(equipment.assetType) && !isCombiner;
    const moduleChildren = isStringForGrid
      ? directChildren.filter(e => isGenerationAsset(e.assetType))
      : [];
    const lazyModuleChildrenPending =
      (equipment.pvModuleCount ?? 0) > 0 &&
      !expandedVirtualStringIds.includes(equipment.id) &&
      (equipment.virtualModulesLazy === true ||
        (!equipment.isVirtual && isStringForGrid && moduleChildren.length === 0));
    const hasChildren =
      directChildren.length > 0 || lazyN > 0 || lazyModuleChildrenPending;
    const displayChildCount = lazyModuleChildrenPending
      ? (equipment.pvModuleCount ?? 0)
      : directChildren.length > 0
        ? directChildren.length
        : lazyN;
    const hasModulesInString = moduleChildren.length > 0;
    const directAssigneeFallback =
      equipment.isAssigned && equipment.assignedTo?.name
        ? { text: equipment.assignedTo.name, isInherited: false }
        : undefined;
    const resourceDisplay =
      resourceDisplayByNodeIdAll.get(equipment.id) ??
      resourceDisplayByNodeId.get(equipment.id) ??
      directAssigneeFallback;

    if (hasModulesInString && !isCombiner && expandedNodes.has(equipment.id)) {
      const moduleCap = TREE_PERF.MAX_MODULE_GRID_CELLS;
      const visibleModules =
        moduleChildren.length > moduleCap ? moduleChildren.slice(0, moduleCap) : moduleChildren;
      const { cols, rows } = calculateGridDimensions(visibleModules.length);
      const gridHeight = rows * MODULE_HEIGHT + (rows - 1) * MODULE_SPACING;

      return (
        <motion.div
          key={equipment.id}
          initial={heavyTree ? false : { opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={heavyTree ? { duration: 0 } : { type: 'spring', stiffness: 300, damping: 28 }}
          className="absolute select-none group"
          style={{
            left: `${x - width / 2}px`,
            top: `${y}px`,
            width: `${width}px`,
            height: `${height}px`,
            ...filterStyle
          }}
        >
          {renderResourceTag(resourceDisplay)}
          <div
            className="relative flex flex-col overflow-hidden transition-[box-shadow,border-color] duration-200 rounded-lg cursor-pointer hover:shadow-md h-full"
            style={{
              backgroundColor: EQUIPMENT_CARD_BG,
              border: `1px solid ${getEquipmentResourceRejection(equipment.id).isRejected ? '#EF4444' : EQUIPMENT_CARD_BORDER}`,
              padding: '6px',
              boxShadow: getEquipmentResourceRejection(equipment.id).isRejected
                ? '0 2px 8px rgba(239,68,68,0.12)'
                : isNodeSelected
                  ? `0 0 0 1px ${EQUIPMENT_CARD_BORDER}, 0 0 0 3px ${EQUIPMENT_CARD_RING_FOCUS}, 0 6px 16px rgba(15, 23, 42, 0.07)`
                  : '0 1px 3px rgba(15, 23, 42, 0.06)'
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (assignmentMode) {
                void toggleEquipmentSelection(equipment.id);
              } else {
                setSelectedNodeId(equipment.id);
                handleEquipmentClick(equipment);
              }
            }}
          >
            {assignmentMode && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-2 -left-2 z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  void toggleEquipmentSelection(equipment.id);
                }}
              >
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className={`w-6 h-6 rounded border-2 flex items-center justify-center cursor-pointer transition-all ${selectedEquipmentIds.has(equipment.id)
                    ? 'bg-blue-600 border-blue-600'
                    : 'bg-white border-gray-300 hover:border-blue-400'
                    }`}
                >
                  {selectedEquipmentIds.has(equipment.id) && (
                    <CheckCircle2 size={14} className="text-white" />
                  )}
                </motion.div>
              </motion.div>
            )}

            {getEquipmentResourceRejection(equipment.id).isRejected && !assignmentMode && (
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                className="absolute top-1 right-1 z-10"
              >
                <div className="flex items-center gap-0.5 px-1.5 py-0.5 bg-red-600 text-white rounded text-[10px] font-semibold tracking-tight">
                  <AlertCircle size={10} />
                  <span>Resource rejected</span>
                </div>
              </motion.div>
            )}

            {/* Header: asset identity + module count */}
            <div className="flex items-center gap-2 shrink-0 py-2 px-0.5 border-b border-slate-200/90">
              <div
                className="w-7 h-7 flex items-center justify-center rounded-md border shrink-0"
                style={{ backgroundColor: `${config.color}14`, borderColor: `${config.color}40` }}
              >
                <Icon size={14} style={{ color: config.color }} />
              </div>
              <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <h4
                    className="font-semibold text-[13px] tracking-tight leading-snug text-slate-900 break-words"
                    title={equipment.name}
                  >
                    {equipment.name}
                  </h4>
                  {equipment.equipmentCode && (
                    <span
                      className="block text-[10px] text-slate-500 font-mono tabular-nums mt-0.5 break-all"
                      title={equipment.equipmentCode}
                    >
                      {equipment.equipmentCode}
                    </span>
                  )}
                </div>
                <span
                  className="text-[11px] shrink-0 flex items-center gap-1 font-semibold tabular-nums"
                  style={{ color: config.color, opacity: 0.9 }}
                >
                  <Grid3x3 size={12} strokeWidth={2} />
                  {moduleChildren.length} modules
                </span>
              </div>
            </div>

            {/* Module grid: solar-panel style — frame, gap background, spaced cells like reference */}
            <div
              className="relative w-full shrink-0 rounded-md border border-slate-300/90 shadow-inner"
              style={{
                height: `${gridHeight + 2 * MODULE_GRID_PADDING}px`,
                backgroundColor: MODULE_GRID_GAP_BG,
                padding: `${MODULE_GRID_PADDING}px`
              }}
            >
              {visibleModules.map((mod, idx) => {
                if (!mod) return null;
                const modConfig = getAssetTypeConfig(mod.assetType, availableAssets, {
                  masterAssetId: mod.masterAssetId,
                  catalogName: mod.projectAssetName
                });
                const row = Math.floor(idx / cols);
                const col = idx % cols;
                const mx = col * (MODULE_WIDTH + MODULE_SPACING);
                const my = row * (MODULE_HEIGHT + MODULE_SPACING);
                const modTitle = (mod.name || `Module ${idx + 1}`).trim();
                const modType = (mod.projectAssetName || mod.assetType || '').trim();
                const skipModStagger =
                  heavyTree || moduleChildren.length > 60;

                return (
                  <motion.div
                    key={mod.id}
                    initial={skipModStagger ? false : { opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={skipModStagger ? { duration: 0 } : { delay: idx * 0.01 }}
                    className="absolute cursor-pointer rounded-sm overflow-hidden flex flex-col items-center justify-center transition-all hover:brightness-110"
                    style={{
                      left: `${mx}px`,
                      top: `${my}px`,
                      width: `${MODULE_WIDTH}px`,
                      height: `${MODULE_HEIGHT}px`,
                      background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 40%, #0F172A 100%)',
                      border: `1px solid ${modConfig.color}66`,
                      boxShadow: `inset 0 1px 0 ${modConfig.color}33`
                    }}
                    title={`${modTitle}${modType ? ` · ${modType}` : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (assignmentMode) {
                        void toggleEquipmentSelection(mod.id);
                      } else {
                        setSelectedNodeId(mod.id);
                        handleEquipmentClick(mod);
                      }
                    }}
                  >
                    {assignmentMode && (
                      <div className="absolute -top-0.5 -left-0.5 z-20">
                        <div
                          className={`w-3.5 h-3.5 rounded border flex items-center justify-center shadow ${selectedEquipmentIds.has(mod.id)
                            ? 'bg-blue-600 border-blue-400'
                            : 'bg-slate-600/90 border-slate-400'
                            }`}
                        >
                          {selectedEquipmentIds.has(mod.id) && (
                            <Check size={8} className="text-white" />
                          )}
                        </div>
                      </div>
                    )}
                    {/* Busbar line (subtle vertical) */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-px h-full bg-slate-500/30" />
                    </div>
                    <Grid3x3 size={9} className="shrink-0 relative z-10" strokeWidth={1.5} style={{ color: modConfig.color }} />
                  </motion.div>
                );
              })}
            </div>

            {moduleChildren.length > moduleCap && (
              <p className="text-[10px] text-slate-600 px-1.5 py-1.5 text-center shrink-0 border-t border-slate-200/90 bg-slate-50 leading-snug font-medium">
                +{moduleChildren.length - moduleCap} modules omitted for performance — open details or zoom in.
              </p>
            )}

            {/* Footer: quick DC/AC cable + delete */}
            <div className="flex items-center justify-end gap-1 flex-wrap mt-2 pt-2 pb-2 border-t border-slate-200/90 shrink-0">
              {!assignmentMode && !isEquipmentOrDescendantsAssigned(equipment.id) && (
                <>
                  {equipmentHasCableChild(equipment, 'dc', visualEquipmentList) && (
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        openCableChildModal(equipment, 'dc');
                      }}
                      className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-md border border-slate-200 bg-white text-[10px] font-semibold text-slate-700 hover:bg-slate-50 hover:border-slate-300"
                      title="Add DC Cable as child"
                    >
                      <Cable size={10} className="opacity-80" />
                      DC
                    </button>
                  )}
                  {equipmentHasCableChild(equipment, 'ac', visualEquipmentList) && (
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        openCableChildModal(equipment, 'ac');
                      }}
                      className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-md border border-rose-200 bg-rose-50/90 text-[10px] font-semibold text-rose-800 hover:bg-rose-100"
                      title="Add AC Cable as child"
                    >
                      <Cable size={9} className="opacity-80" />
                      AC
                    </button>
                  )}
                </>
              )}
              {!isEquipmentOrDescendantsAssigned(equipment.id) && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleDeleteEquipment(equipment.id); }}
                  className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  title="Delete"
                >
                  <Trash2 size={10} />
                </button>
              )}
            </div>
          </div>
        </motion.div>
      );
    }

    return (
      <motion.div
        key={equipment.id}
        initial={heavyTree ? false : { opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={heavyTree ? { duration: 0 } : { type: 'spring', stiffness: 300, damping: 28 }}
        className="absolute select-none group"
        style={{
          left: `${x - width / 2}px`,
          top: `${y}px`,
          width: `${width}px`,
          ...filterStyle
        }}
      >
        {renderResourceTag(resourceDisplay)}
        <div
          className="relative overflow-x-hidden overflow-y-visible transition-[box-shadow,border-color] duration-200 rounded-lg cursor-pointer hover:shadow-md flex items-stretch min-h-[52px]"
          style={{
            backgroundColor: EQUIPMENT_CARD_BG,
            border: `1px solid ${getEquipmentResourceRejection(equipment.id).isRejected ? '#EF4444' : EQUIPMENT_CARD_BORDER}`,
            boxShadow: getEquipmentResourceRejection(equipment.id).isRejected
              ? '0 2px 8px rgba(239,68,68,0.12)'
              : isNodeSelected
                ? `0 0 0 1px ${EQUIPMENT_CARD_BORDER}, 0 0 0 3px ${EQUIPMENT_CARD_RING_FOCUS}, 0 6px 16px rgba(15, 23, 42, 0.07)`
                : '0 1px 3px rgba(15, 23, 42, 0.06)'
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (assignmentMode) {
              void toggleEquipmentSelection(equipment.id);
            } else {
              setSelectedNodeId(equipment.id);
              handleEquipmentClick(equipment);
            }
          }}
        >
          {/* Checkbox for assignment mode */}
          {assignmentMode && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -top-2 -left-2 z-20"
              onClick={(e) => {
                e.stopPropagation();
                void toggleEquipmentSelection(equipment.id);
              }}
            >
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center cursor-pointer transition-all shadow ${selectedEquipmentIds.has(equipment.id)
                  ? 'bg-blue-600 border-blue-600'
                  : 'bg-white border-gray-300 hover:border-blue-400'
                  }`}
              >
                {selectedEquipmentIds.has(equipment.id) && (
                  <CheckCircle2 size={14} className="text-white" />
                )}
              </motion.div>
            </motion.div>
          )}

          {/* Resource Rejection Badge */}
          {getEquipmentResourceRejection(equipment.id).isRejected && !assignmentMode && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -top-1 -right-1 z-20"
            >
              <div className="flex items-center gap-0.5 px-1.5 py-0.5 bg-red-600 text-white rounded text-[10px] font-semibold tracking-tight">
                <AlertCircle size={9} />
                <span>Rejected</span>
              </div>
            </motion.div>
          )}

          {/* Single row: type chip, identity, expand, actions */}
          <div className="flex items-start gap-2.5 py-2.5 px-3 flex-1 min-w-0 relative z-10">
            <div
              className="w-8 h-8 flex items-center justify-center rounded-md border shrink-0 mt-0.5"
              style={{ backgroundColor: `${config.color}14`, borderColor: `${config.color}40` }}
            >
              <Icon size={15} style={{ color: config.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <h4
                className="font-semibold text-[13px] text-slate-900 leading-snug block tracking-tight break-words"
                title={equipment.name}
              >
                {equipment.name}
              </h4>
              {equipment.equipmentCode && (
                <span
                  className="block text-[10px] text-slate-500 font-mono tabular-nums leading-snug mt-0.5 break-all"
                  title={equipment.equipmentCode}
                >
                  {equipment.equipmentCode}
                </span>
              )}
              <p className="text-[11px] flex items-baseline gap-1.5 flex-wrap gap-y-1 min-w-0 mt-1">
                <span className="font-semibold text-slate-600 break-words" title={typeLabel}>
                  {typeLabel}
                </span>
                {hasChildren && (
                  <>
                    <span className="text-slate-300 shrink-0 font-light">|</span>
                    <span className="text-slate-600 shrink-0 font-medium tabular-nums whitespace-nowrap">
                      {displayChildCount} child{displayChildCount !== 1 ? 'ren' : ''}
                    </span>
                  </>
                )}
              </p>
            </div>
            {hasChildren && (
              <button
                type="button"
                disabled={
                  loadingChildrenForParentId === equipment.id || hydratingEquipmentId === equipment.id
                }
                onClick={(e) => {
                  e.stopPropagation();
                  void (async () => {
                    const willExpand = !expandedNodes.has(equipment.id);
                    if (willExpand && needsLazyFetch) {
                      await loadChildrenForEquipment(equipment.id);
                    }
                    if (willExpand && equipmentNeedsSolarLayoutHydrate(equipment, true)) {
                      await hydrateEquipmentRow(equipment.id);
                    }
                    if (willExpand) {
                      const deferredMods =
                        (equipment.pvModuleCount ?? 0) > 0 &&
                        !expandedVirtualStringIds.includes(equipment.id) &&
                        (equipment.virtualModulesLazy === true ||
                          (!equipment.isVirtual &&
                            isStringForGrid &&
                            moduleChildren.length === 0));

                      const treeKey = getEquipmentTreeQueryKey(projectId || '', {});
                      const cached = queryClient.getQueryData<EquipmentResponse>(treeKey);
                      const bulkIds = collectBulkStringModuleExpansionIds(equipment, cached?.data ?? []);

                      if (bulkIds.length > 0 || deferredMods) {
                        setExpandedVirtualStringIds((prev) => {
                          const set = new Set(prev);
                          for (const id of bulkIds) set.add(id);
                          if (deferredMods) set.add(equipment.id);
                          return Array.from(set);
                        });
                      }
                    } else {
                      setExpandedVirtualStringIds((prev) => {
                        let next = prev.filter((x) => x !== equipment.id);
                        const clearsNestedVirtualStrings =
                          isCombiner ||
                          isSolarDcLayoutEquipmentType(equipment.assetType || '') ||
                          (isInverterEquipmentType(equipment.assetType) &&
                            equipment.solarDcLayout &&
                            isSolarDcLayout(equipment.solarDcLayout) &&
                            equipment.solarDcLayout.combinerBoxCount === 0);
                        if (clearsNestedVirtualStrings) {
                          const prefix = `${equipment.id}::str-`;
                          next = next.filter((id) => !id.startsWith(prefix));
                        }
                        if (isInverterEquipmentType(equipment.assetType)) {
                          const treeKey = getEquipmentTreeQueryKey(projectId || '', {});
                          const cached = queryClient.getQueryData<EquipmentResponse>(treeKey);
                          const rows = cached?.data ?? [];
                          const childStringIds = new Set<string>();
                          for (const r of rows) {
                            const p = normalizeEquipmentId(r.parentId ?? r.parentEquipmentId);
                            if (p !== equipment.id) continue;
                            const at = String(r.assetType || r.hierarchyLevel || '');
                            if (!isStringAsset(at) || isCombinerBoxAssetType(at)) continue;
                            const sid = normalizeEquipmentId(r._id ?? r.id);
                            if (sid) childStringIds.add(sid);
                          }
                          next = next.filter((id) => !childStringIds.has(id));
                        }
                        return next;
                      });
                    }
                    startTransition(() => {
                      setExpandedNodes(prev => {
                        const next = new Set(prev);
                        if (next.has(equipment.id)) next.delete(equipment.id);
                        else next.add(equipment.id);
                        return next;
                      });
                    });
                  })();
                }}
                className={`self-start mt-1 p-1.5 rounded-md shrink-0 transition-colors ${expandedNodes.has(equipment.id)
                  ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-200/90'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200/90'
                  } ${loadingChildrenForParentId === equipment.id || hydratingEquipmentId === equipment.id ? 'opacity-60 cursor-wait' : ''}`}
                title={expandedNodes.has(equipment.id) ? 'Collapse' : 'Expand'}
              >
                {expandedNodes.has(equipment.id) ? <ChevronDown size={14} strokeWidth={2.25} /> : <ChevronRight size={14} strokeWidth={2.25} />}
              </button>
            )}
            {!assignmentMode && !isEquipmentOrDescendantsAssigned(equipment.id) && (
              <>
                {equipmentHasCableChild(equipment, 'dc', visualEquipmentList) && (
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      openCableChildModal(equipment, 'dc');
                    }}
                    className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-md border border-slate-200 bg-white text-[10px] font-semibold text-slate-700 hover:bg-slate-50 shrink-0"
                    title="Add DC Cable as child"
                  >
                    <Cable size={10} />
                    DC
                  </button>
                )}
                {equipmentHasCableChild(equipment, 'ac', visualEquipmentList) && (
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      openCableChildModal(equipment, 'ac');
                    }}
                    className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-md border border-rose-200 bg-rose-50/90 text-[10px] font-semibold text-rose-800 hover:bg-rose-100 shrink-0"
                    title="Add AC Cable as child"
                  >
                    <Cable size={10} />
                    AC
                  </button>
                )}
              </>
            )}
            {!isEquipmentOrDescendantsAssigned(equipment.id) && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleDeleteEquipment(equipment.id); }}
                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors shrink-0"
                title="Delete"
              >
                <Trash2 size={12} strokeWidth={2} />
              </button>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="equipment-network-scope h-full min-h-0 flex flex-col">
      <div className="h-full flex flex-col bg-white relative z-0 min-h-0">
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap');
          .equipment-network-scope {
            font-family: 'IBM Plex Sans', 'Segoe UI', system-ui, -apple-system, sans-serif;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
          }
          .equipment-tree-canvas {
            background-color: #f8fafc;
            background-image:
              radial-gradient(circle at center, rgba(148, 163, 184, 0.22) 1px, transparent 1px);
            background-size: 20px 20px;
            background-position: 0 0;
          }
          .custom-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
          .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; }
          .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
          .btn-premium { font-family: inherit; font-weight: 600; letter-spacing: -0.015em; }
        `}</style>

        {/* Collapsible Assets section + Equipment tree below */}
        <div className="flex-1 flex flex-col min-h-0">
          {(() => {
            // Aggregate summary for the collapsed header. Computed inline because it depends on
            // getAssetRejectionInfo which is declared later in this component body.
            const totalAssets = projectAssetsVisibleInStrip.length;
            const totalEquipmentCount = equipmentList.length;
            let rejectedAssetsCount = 0;
            let updatedAssetsCount = 0;
            for (const a of projectAssetsVisibleInStrip) {
              const info = getAssetRejectionInfo((a as any)._id);
              if (info.hasRejections) rejectedAssetsCount++;
              else if (info.hasUpdated) updatedAssetsCount++;
            }
            return (
              <div className="shrink-0 border-b border-gray-200/80 bg-white relative z-[2]">
                {/* Collapsible header bar — always visible. Click anywhere to toggle. */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setAssetsExpanded(v => !v)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setAssetsExpanded(v => !v);
                    }
                  }}
                  className="flex items-center justify-between gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors"
                  aria-expanded={assetsExpanded}
                  aria-controls="assets-section-body"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-wrap">
                    <span className="text-slate-500 shrink-0">
                      {assetsExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </span>
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-blue-600 border border-blue-700/30 shrink-0">
                      <Box size={14} className="text-white" />
                    </div>
                    <h2 className="font-semibold text-sm text-gray-800 tracking-tight shrink-0">Assets</h2>
                    {/* Header pills — show all counts at a glance even when collapsed */}
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold tabular-nums bg-blue-50 text-blue-700 border border-blue-100"
                      title="Total assets in this project"
                    >
                      <Layers size={11} />
                      {totalAssets} {totalAssets === 1 ? 'asset' : 'assets'}
                    </span>
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold tabular-nums bg-emerald-50 text-emerald-700 border border-emerald-100"
                      title="Total equipment under all assets"
                    >
                      <Package size={11} />
                      {totalEquipmentCount} eq
                    </span>
                    {rejectedAssetsCount > 0 && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold tabular-nums bg-red-50 text-red-700 border border-red-200"
                        title="Assets with rejected checkpoints"
                      >
                        <AlertCircle size={11} />
                        {rejectedAssetsCount} rejected
                      </span>
                    )}
                    {updatedAssetsCount > 0 && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold tabular-nums bg-amber-50 text-amber-700 border border-amber-200"
                        title="Assets with updated checkpoints"
                      >
                        <CheckCircle2 size={11} />
                        {updatedAssetsCount} updated
                      </span>
                    )}
                  </div>

                  <div
                    className="flex items-center gap-1.5 flex-wrap shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {onAddMasterAsset && (
                      <button
                        onClick={onAddMasterAsset}
                        className="btn-premium flex items-center gap-1.5 px-3 py-1.5 text-[12px] bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-lg font-semibold shadow-sm hover:shadow-md transition-all duration-200 ease-out active:scale-[0.98]"
                      >
                        <Plus size={13} />
                        <span>Add Asset</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Expanded body */}
                <AnimatePresence initial={false}>
                  {assetsExpanded && (
                    <motion.div
                      key="assets-body"
                      id="assets-section-body"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.18, ease: 'easeOut' }}
                      className="overflow-hidden bg-slate-50 border-t border-slate-200/70"
                    >
                      <div className="px-3 py-2.5">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3 mb-2">
                          <div className="flex items-center gap-1.5 min-w-0 shrink-0">
                            <CheckCircle size={12} className="text-blue-600 shrink-0" />
                            <h3 className="font-semibold text-[11px] text-gray-700 truncate">Project Assets</h3>
                            {assetSearchQuery.trim() && (
                              <span className="text-[10px] text-slate-500">
                                ({filteredProjectAssets.length}/{totalAssets} match)
                              </span>
                            )}
                          </div>
                          {totalAssets > 0 ? (
                            <div className="relative w-full sm:w-64 sm:max-w-xs shrink-0">
                              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" aria-hidden />
                              <input
                                id="project-asset-search-input"
                                type="search"
                                value={assetSearchQuery}
                                onChange={e => setAssetSearchQuery(e.target.value)}
                                placeholder="Search assets…"
                                className="w-full rounded-md border border-slate-200 bg-white py-1.5 pl-7 pr-7 text-[12px] text-gray-800 placeholder:text-slate-400 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                                aria-label="Search assets"
                              />
                              {assetSearchQuery ? (
                                <button
                                  type="button"
                                  className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                                  onClick={() => setAssetSearchQuery('')}
                                  aria-label="Clear search"
                                >
                                  <X size={12} />
                                </button>
                              ) : null}
                            </div>
                          ) : null}
                        </div>

                        {projectAssets && projectAssets.length > 0 && (
                          <>
              <div className="mt-2 grid grid-cols-2 gap-2 pb-1 sm:grid-cols-4 lg:grid-cols-8">
                  {filteredProjectAssets.map((asset, index) => {
                    const config = getAssetTypeConfig(asset.name, availableAssets);
                    const Icon = config.icon;
                    // Equipment count comes from the dedicated Project Assets API
                    // (`/project-assets/with-checkpoints`); do not derive it from the
                    // Site Equipment Network's `equipmentList`.
                    const equipmentCount = (asset as any).equipmentCount ?? 0;
                    const rejectionInfo = getAssetRejectionInfo(asset._id);
                    return (
                      <motion.div
                        key={asset._id}
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.03 }}
                        className="min-w-0 w-full"
                      >
                        <div
                          className={`bg-white rounded-md border shadow-sm hover:shadow transition-all cursor-pointer ${rejectionInfo.hasRejections
                            ? 'border-red-300 ring-1 ring-red-100'
                            : rejectionInfo.hasUpdated
                              ? 'border-amber-300 ring-1 ring-amber-100'
                              : 'border-gray-200 hover:border-blue-300'
                            }`}
                        >
                          <div className="p-1.5">
                            <div className="mb-1 flex items-start gap-1">
                              <div
                                className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded ${rejectionInfo.hasRejections ? 'ring-1 ring-red-400' : rejectionInfo.hasUpdated ? 'ring-1 ring-amber-400' : ''}`}
                                style={{
                                  background: rejectionInfo.hasRejections
                                    ? 'linear-gradient(135deg, #FEE2E2, #FCA5A5)'
                                    : rejectionInfo.hasUpdated
                                      ? 'linear-gradient(135deg, #FEF3C7, #FDE68A)'
                                      : `linear-gradient(135deg, ${config.color}15, ${config.color}28)`,
                                  borderColor: rejectionInfo.hasRejections ? '#EF4444' : rejectionInfo.hasUpdated ? '#D97706' : config.color + '40',
                                  borderWidth: '1px'
                                }}
                              >
                                <Icon size={12} style={{ color: rejectionInfo.hasRejections ? '#DC2626' : rejectionInfo.hasUpdated ? '#B45309' : config.color }} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <h4
                                  className="break-words text-[10px] font-semibold leading-snug text-gray-900 [overflow-wrap:anywhere] whitespace-normal"
                                  title={asset.name}
                                >
                                  {asset.name}
                                </h4>
                                <p className="mt-0.5 text-[8px] tabular-nums text-gray-500">{equipmentCount} eq</p>
                              </div>
                              {rejectionInfo.hasRejections && (
                                <div className="flex items-center px-1 py-px bg-red-600 text-white rounded text-[7px] font-semibold shrink-0" title="Checkpoints still rejected">
                                  {rejectionInfo.rejectedCount}
                                </div>
                              )}
                              {!rejectionInfo.hasRejections && rejectionInfo.hasUpdated && (
                                <div className="flex items-center px-1 py-px bg-amber-500 text-white rounded text-[7px] font-semibold shrink-0" title="Checkpoints updated after rejection">
                                  {rejectionInfo.modifiedCount}
                                </div>
                              )}
                            </div>
                            <div className="flex gap-0.5">
                              <button
                                type="button"
                                title="Checkpoints"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedAssetForCheckpoints(asset);
                                  setShowCheckpointsDrawer(true);
                                }}
                                className={`btn-premium flex-1 flex items-center justify-center gap-0.5 px-1 py-1 border rounded text-[9px] font-semibold transition-all duration-200 ${rejectionInfo.hasRejections
                                  ? 'bg-red-50 hover:bg-red-100 border-red-200 text-red-700 hover:border-red-300'
                                  : rejectionInfo.hasUpdated
                                    ? 'bg-amber-50 hover:bg-amber-100 border-amber-200 text-amber-700 hover:border-amber-300'
                                    : 'bg-slate-50 hover:bg-blue-50 border-slate-200 hover:border-blue-200 text-slate-700'
                                  }`}
                              >
                                <CheckCircle2 size={9} className="shrink-0" />
                                <span className="truncate leading-none">Ckpt</span>
                              </button>
                              {equipmentCount === 0 && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveProjectAsset(asset);
                                  }}
                                  className="btn-premium flex items-center justify-center px-1 py-1 border rounded text-[10px] font-semibold bg-white hover:bg-red-50 border-slate-200 hover:border-red-300 text-red-600 transition-all duration-200"
                                  title="Remove asset"
                                >
                                  <Trash2 size={9} />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
                {filteredProjectAssets.length === 0 && assetSearchQuery.trim() && (
                  <p className="mt-2 text-center text-[8px] text-slate-500">No matches</p>
                )}
                </>
              )}

                        {(!projectAssets || projectAssets.length === 0) && (
                          <div className="mt-2 text-center py-6 bg-white rounded-xl border-2 border-dashed border-gray-200">
                            <p className="text-[11px] text-gray-500 mb-2">No assets configured</p>
                            {onAddMasterAsset && (
                              <button
                                onClick={onAddMasterAsset}
                                className="btn-premium px-4 py-2 text-[13px] bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200 ease-out active:scale-[0.98]"
                              >
                                <Plus size={14} /> Add Asset
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })()}

          {/* Equipment tree — full width below assets row */}
          <section className="flex-1 flex flex-col min-h-0 min-w-0 bg-slate-100/80">
          <div className="flex-1 flex flex-col min-h-0">
          {!equipmentLoading && !equipmentError && projectAssets && projectAssets.length > 0 && (
            <div className="flex-shrink-0 border-b border-slate-200/90 bg-white px-3 py-3 shadow-[0_1px_0_rgba(15,23,42,0.04)]">
              <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex flex-col gap-2 min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-600 shadow-sm shrink-0">
                    <Network size={14} className="text-white" />
                  </div>
                  <h3 className="text-sm font-semibold text-slate-900 tracking-tight shrink-0">
                    Site Equipment Network
                  </h3>
                </div>
                {/* Stat boxes — proper visual cards for counts */}
                <div className="flex flex-wrap items-stretch gap-2">
                  {equipmentSummaryLoading ? (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-[11px] text-slate-400 tabular-nums">
                      Loading…
                    </div>
                  ) : (
                    <>
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50/80 px-3 py-1.5 shadow-sm">
                        <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                          <Package size={11} />
                          Equipment
                        </div>
                        <div className="text-sm font-bold text-emerald-900 tabular-nums leading-tight">
                          {persistedEquipmentTotal.toLocaleString()}
                        </div>
                      </div>
                      {persistedModuleTotal > 0 && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-1.5 shadow-sm">
                          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                            <Sun size={11} />
                            PV Modules
                          </div>
                          <div className="text-sm font-bold text-amber-900 tabular-nums leading-tight">
                            {persistedModuleTotal.toLocaleString()}
                          </div>
                        </div>
                      )}
                      {visualEquipmentList.length > 0 && (
                        <div className="rounded-lg border border-blue-200 bg-blue-50/80 px-3 py-1.5 shadow-sm">
                          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700">
                            <Layers size={11} />
                            Visible
                          </div>
                          <div className="text-sm font-bold text-blue-900 tabular-nums leading-tight">
                            {visualEquipmentList.length.toLocaleString()}
                          </div>
                        </div>
                      )}
                      {pendingChangesTotal > 0 && (
                        <button
                          type="button"
                          onClick={() => navigate('/approvals/dashboard')}
                          title="Pending head approval — task generation is paused for these items"
                          className="rounded-lg border border-amber-300 bg-amber-50/90 px-3 py-1.5 shadow-sm text-left hover:bg-amber-100 hover:border-amber-400 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-300"
                        >
                          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                            <AlertCircle size={11} />
                            Pending approval
                          </div>
                          <div className="text-sm font-bold text-amber-900 tabular-nums leading-tight">
                            {pendingChangesTotal.toLocaleString()}
                          </div>
                        </button>
                      )}
                    </>
                  )}
                </div>
                {!equipmentSummaryLoading && equipmentBreakdownWithModules.length > 0 && (
                  <p
                    className="text-[11px] text-slate-600 leading-snug line-clamp-2 break-words"
                    title={equipmentBreakdownWithModules.map((b) => `${b.count}× ${b.label}`).join(' · ')}
                  >
                    {formatProjectEquipmentBreakdownShort(equipmentBreakdownWithModules)}
                  </p>
                )}
                <p className="text-[11px] text-slate-500 leading-snug max-w-3xl">
                  One-line style hierarchy (generation → strings → conversion). Expand parents to load children. Pan
                  the canvas and use zoom at bottom-left.
                </p>
              </div>
              <div className="flex flex-col items-stretch sm:items-end gap-1.5 min-w-0">
                {hasAnyRejectedResourceAssignment && !assignmentMode && visualEquipmentList.length > 0 && (
                  <div
                    className="flex items-start gap-1.5 rounded-lg border border-red-300 bg-red-50 px-2 py-1.5 shadow-sm sm:max-w-sm"
                    role="status"
                  >
                    <AlertCircle size={14} className="text-red-600 shrink-0 mt-0.5" aria-hidden />
                    <p className="text-[10px] font-medium text-red-900 leading-snug text-left">
                      Resource assignment was rejected on one or more items. Use{' '}
                      <span className="font-semibold">Assign resource</span> to update equipment, then use{' '}
                      <span className="font-semibold">Submit Review</span> in the project approval flow to send changes
                      for review again.
                    </p>
                  </div>
                )}
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  {visualEquipmentList.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setAssignmentMode(!assignmentMode);
                        if (assignmentMode) {
                          clearSelection();
                        }
                      }}
                      title={
                        !assignmentMode && hasAnyRejectedResourceAssignment
                          ? 'Update assignments, then Submit Review to resubmit'
                          : undefined
                      }
                      className={`btn-premium flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] rounded-lg font-semibold shadow-sm transition-all duration-200 ease-out active:scale-[0.98] ${
                        assignmentMode
                          ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white'
                          : hasAnyRejectedResourceAssignment
                            ? 'bg-red-50 text-red-800 border-2 border-red-400 ring-2 ring-red-200/90 hover:bg-red-100 hover:border-red-500'
                            : 'bg-white text-slate-700 border border-slate-200 hover:border-blue-300 hover:bg-blue-50/80'
                      }`}
                    >
                      <UserCheck size={14} className={!assignmentMode && hasAnyRejectedResourceAssignment ? 'text-red-700' : undefined} />
                      <span className="hidden sm:inline">{assignmentMode ? 'Exit assign' : 'Assign resource'}</span>
                      <span className="sm:hidden">{assignmentMode ? 'Exit' : 'Assign'}</span>
                    </button>
                  )}
                  {showSolarSetupGuide && (
                    <button
                      type="button"
                      onClick={() => setSolarWizardOpen(true)}
                      disabled={isBulkCreatingEquipment}
                      className="btn-premium flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] rounded-lg font-semibold border border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100 transition-all duration-200 ease-out active:scale-[0.98] disabled:opacity-40"
                    >
                      <Sun size={14} className="text-amber-600" />
                      <span className="hidden sm:inline">AI Project Architecte</span>
                      <span className="sm:hidden">Solar guide</span>
                    </button>
                  )}
                  {visualEquipmentList.length > 0 && (
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setFilterPanelOpen(v => !v)}
                        title="Advanced filter"
                        aria-expanded={filterPanelOpen}
                        className={`btn-premium flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] rounded-lg font-semibold transition-all duration-200 ease-out active:scale-[0.98] shadow-sm ${
                          isFilterActive
                            ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white border border-violet-700/30'
                            : 'border border-slate-300 bg-white text-slate-700 hover:border-violet-300 hover:bg-violet-50/80 hover:text-violet-700'
                        }`}
                      >
                        <SlidersHorizontal size={14} />
                        <span className="hidden sm:inline">Filter</span>
                        {isFilterActive && (
                          <span className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full text-[10px] font-bold bg-white/25 text-white">
                            1
                          </span>
                        )}
                      </button>
                      {filterPanelOpen && (
                        <>
                          {/* Click-outside backdrop */}
                          <div
                            className="fixed inset-0 z-40"
                            onClick={() => setFilterPanelOpen(false)}
                            aria-hidden
                          />
                          <motion.div
                            initial={{ opacity: 0, y: -4, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            transition={{ duration: 0.12, ease: 'easeOut' }}
                            className="absolute right-0 top-full mt-2 w-72 sm:w-80 z-50 rounded-xl border border-slate-200 bg-white shadow-xl ring-1 ring-black/5"
                            role="dialog"
                            aria-label="Equipment filter"
                          >
                            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
                              <div className="flex items-center gap-1.5 text-slate-700">
                                <SlidersHorizontal size={13} />
                                <span className="text-[12px] font-semibold">Highlight Equipment</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => setFilterPanelOpen(false)}
                                className="p-0.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                                title="Close"
                              >
                                <X size={14} />
                              </button>
                            </div>
                            <div className="px-3 py-2.5 space-y-2.5 max-h-[60vh] overflow-y-auto custom-scrollbar">
                              {/* All option */}
                              <label className="flex items-start gap-2 p-2 rounded-lg border border-transparent hover:border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
                                <input
                                  type="radio"
                                  name="equipment-filter"
                                  className="mt-0.5 accent-violet-600"
                                  checked={equipmentFilter.mode === 'none'}
                                  onChange={() => setEquipmentFilter({ mode: 'none' })}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="text-[12px] font-semibold text-slate-800">All equipment</div>
                                  <div className="text-[10px] text-slate-500">Show every equipment at full visibility</div>
                                </div>
                              </label>
                              {/* Unassigned option */}
                              <label className="flex items-start gap-2 p-2 rounded-lg border border-transparent hover:border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
                                <input
                                  type="radio"
                                  name="equipment-filter"
                                  className="mt-0.5 accent-violet-600"
                                  checked={equipmentFilter.mode === 'unassigned'}
                                  onChange={() => setEquipmentFilter({ mode: 'unassigned' })}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-800">
                                    <UserX size={12} className="text-amber-600" />
                                    Unassigned only
                                  </div>
                                  <div className="text-[10px] text-slate-500">Highlight equipment not assigned to any resource</div>
                                </div>
                              </label>
                              {/* By resource option */}
                              <div className="rounded-lg border border-transparent">
                                <label className="flex items-start gap-2 p-2 rounded-lg border border-transparent hover:border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
                                  <input
                                    type="radio"
                                    name="equipment-filter"
                                    className="mt-0.5 accent-violet-600"
                                    checked={equipmentFilter.mode === 'byResource'}
                                    onChange={() =>
                                      setEquipmentFilter({
                                        mode: 'byResource',
                                        resourceId: equipmentFilter.resourceId
                                      })
                                    }
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-800">
                                      <Users size={12} className="text-blue-600" />
                                      By resource
                                    </div>
                                    <div className="text-[10px] text-slate-500">
                                      Pick a resource to highlight their equipment
                                    </div>
                                  </div>
                                </label>
                                {equipmentFilter.mode === 'byResource' && (
                                  <div className="mt-1 pl-6 pr-2 pb-1 space-y-1">
                                    {resourceAssignmentCounts.size === 0 ? (
                                      <p className="text-[11px] text-slate-500 italic px-1 py-2">
                                        No resources assigned yet.
                                      </p>
                                    ) : (
                                      [...resourceAssignmentCounts.entries()]
                                        .sort((a, b) => a[1].name.localeCompare(b[1].name))
                                        .map(([rid, info]) => {
                                          const selected = equipmentFilter.resourceId === rid;
                                          return (
                                            <button
                                              key={rid}
                                              type="button"
                                              onClick={() =>
                                                setEquipmentFilter({ mode: 'byResource', resourceId: rid })
                                              }
                                              className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-left text-[12px] transition-colors ${
                                                selected
                                                  ? 'bg-blue-600 text-white border border-blue-700/30 shadow-sm'
                                                  : 'bg-slate-50 hover:bg-blue-50 text-slate-700 border border-slate-200'
                                              }`}
                                            >
                                              <span className="font-medium truncate">{info.name}</span>
                                              <span
                                                className={`inline-flex items-center justify-center min-w-[20px] px-1.5 py-px rounded-full text-[10px] font-semibold tabular-nums ${
                                                  selected ? 'bg-white/25 text-white' : 'bg-blue-100 text-blue-700'
                                                }`}
                                              >
                                                {info.count}
                                              </span>
                                            </button>
                                          );
                                        })
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            {isFilterActive && (
                              <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-slate-100 bg-slate-50/60">
                                <span className="text-[10px] text-slate-500">Filter is active</span>
                                <button
                                  type="button"
                                  onClick={() => setEquipmentFilter({ mode: 'none' })}
                                  className="text-[11px] font-semibold text-slate-600 hover:text-violet-700 px-2 py-1 rounded hover:bg-violet-50 transition-colors"
                                >
                                  Clear filter
                                </button>
                              </div>
                            )}
                          </motion.div>
                        </>
                      )}
                    </div>
                  )}
                  {visualEquipmentList.length > 0 && !fullViewOpen && (
                    <button
                      type="button"
                      onClick={() => setFullViewOpen(true)}
                      title="Open Site Equipment Network in full view"
                      className="btn-premium flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] rounded-lg font-semibold border border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50/80 hover:text-blue-700 transition-all duration-200 ease-out active:scale-[0.98] shadow-sm"
                    >
                      <Maximize2 size={14} />
                      <span className="hidden sm:inline">Full View</span>
                      <span className="sm:hidden">Full</span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setChildSelectionState({
                        open: true,
                        parentId: null,
                        parentType: null,
                        warningOpen: false,
                        selectedAsset: null
                      });
                    }}
                    disabled={isBulkCreatingEquipment}
                    className={`btn-premium flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] rounded-lg font-semibold transition-all duration-200 ease-out active:scale-[0.98] ${
                      isBulkCreatingEquipment
                        ? 'bg-slate-400 cursor-not-allowed text-white shadow-none'
                        : 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-md hover:shadow-lg'
                    }`}
                  >
                    {isBulkCreatingEquipment ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Plus size={14} />
                    )}
                    <span>{isBulkCreatingEquipment ? 'Adding…' : 'Add equipment'}</span>
                  </button>
                </div>
              </div>
              </div>
            </div>
          )}
          {/* Active filter strip — subtle hint above the canvas with quick clear action */}
          {isFilterActive && visualEquipmentList.length > 0 && (
            <div className="flex-shrink-0 flex items-center justify-between gap-2 border-b border-violet-200 bg-violet-50/80 px-3 py-1.5">
              <div className="flex items-center gap-1.5 text-[11px] text-violet-800 min-w-0">
                <SlidersHorizontal size={12} className="shrink-0" />
                <span className="font-semibold shrink-0">Filter active:</span>
                <span className="truncate">
                  {equipmentFilter.mode === 'unassigned'
                    ? 'Unassigned equipment highlighted'
                    : equipmentFilter.mode === 'byResource' && equipmentFilter.resourceId
                      ? `Highlighting ${
                          resourceAssignmentCounts.get(equipmentFilter.resourceId)?.name ?? 'resource'
                        }'s equipment`
                      : ''}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setEquipmentFilter({ mode: 'none' })}
                className="text-[11px] font-semibold text-violet-700 hover:text-violet-900 px-2 py-0.5 rounded hover:bg-white/60 transition-colors shrink-0"
              >
                Clear
              </button>
            </div>
          )}
          <div className="flex-1 overflow-auto relative custom-scrollbar bg-slate-100/80 min-h-0 [contain:layout]">
            {/* Loading State - Data is being fetched */}
            {equipmentLoading && (
              <TreeLoadingState message="Loading equipment hierarchy..." />
            )}

            {/* Error State - Failed to fetch equipment */}
            {equipmentError && !equipmentLoading && (
              <TreeErrorState
                error={equipmentErrorDetails}
                onRetry={() => {
                  void refetchEquipment();
                  void refetchEquipmentSummary();
                }}
              />
            )}

            {/* Empty State - No Equipment (only show when not loading and no error) */}
            {!equipmentLoading && !equipmentError && visualEquipmentList.length === 0 && (
              <div className="flex items-center justify-center h-full p-8">
                <div className="max-w-2xl w-full">
                  {projectAssets && projectAssets.length > 0 ? (
                    /* Has project assets - show option to add equipment */
                    <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-gray-200">
                      <div className="text-center mb-6">
                        <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg mx-auto mb-4">
                          <Network size={32} className="text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          Ready to Build Your Equipment Hierarchy
                        </h3>
                        <p className="text-gray-600 text-sm">
                          {projectAssetsVisibleInStrip.length > 0 ? (
                            <>
                              You have {projectAssetsVisibleInStrip.length} asset
                              {projectAssetsVisibleInStrip.length !== 1 ? 's' : ''} configured. Choose a type below to
                              create your first equipment, or use Add equipment.
                            </>
                          ) : (
                            <>
                              Use <span className="font-semibold">Add equipment</span> to start your hierarchy
                              (String groupers are not listed as catalog tiles).
                            </>
                          )}
                        </p>
                      </div>

                      {projectAssetsVisibleInStrip.length > 0 && (
                      <div className="space-y-3 mb-6">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Available Project Assets
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {projectAssetsVisibleInStrip.map((asset) => {
                            const config = getAssetTypeConfig(asset.name, availableAssets);
                            const Icon = config.icon;

                            return (
                              <button
                                key={asset._id}
                                onClick={() => {
                                  console.log('Asset clicked:', asset);
                                  // Open modal to add equipment of this type
                                  setAddModalState({
                                    open: true,
                                    ghost: {
                                      masterAssetId: asset.masterAssetId,
                                      projectAssetId: asset._id,  // Add projectAssetId here
                                      assetName: asset.name,
                                      assetType: asset.name,
                                      parentId: null,
                                      config: {
                                        unitsPerParent: 1,
                                        namingPattern: '{asset}-{seq}'
                                      }
                                    },
                                    quantity: 1,
                                    namingPattern: '{asset}-{seq}',
                                    documents: []
                                  });
                                }}
                                className="btn-premium flex items-center gap-3 p-4 bg-gradient-to-br from-white to-slate-50/80 border border-slate-200 rounded-xl hover:border-emerald-400 hover:shadow-lg transition-all duration-200 ease-out group text-left text-slate-800"
                              >
                                <div
                                  className="w-12 h-12 flex items-center justify-center rounded-xl shadow-md group-hover:shadow-lg transition-all flex-shrink-0 group-hover:scale-110"
                                  style={{
                                    background: `linear-gradient(135deg, ${config.color}15, ${config.color}30)`,
                                    borderColor: config.color + '40',
                                    borderWidth: '2px'
                                  }}
                                >
                                  <Icon size={20} style={{ color: config.color }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-semibold text-sm text-gray-900 group-hover:text-green-600 transition-colors truncate">
                                    {asset.name}
                                  </h4>
                                  <p className="text-xs text-gray-500">
                                    Click to add equipment
                                  </p>
                                </div>
                                <Plus size={20} className="text-gray-400 group-hover:text-green-600 transition-colors flex-shrink-0" />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      )}

                      <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <AlertCircle size={16} className="text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-green-900 mb-1 text-sm">Getting Started</h4>
                            <p className="text-xs text-green-800">
                              {projectAssetsVisibleInStrip.length > 0 ? (
                                <>
                                  Click on any asset type above to create your first equipment. You can build a
                                  hierarchy by adding child equipment to parent equipment.
                                </>
                              ) : (
                                <>
                                  Use <span className="font-semibold">Add equipment</span> to create your first row.
                                  You can build a hierarchy by adding child equipment under parents.
                                </>
                              )}
                            </p>
                            {showSolarSetupGuide && (
                              <button
                                type="button"
                                onClick={() => setSolarWizardOpen(true)}
                                className="mt-3 inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-white px-3 py-2 text-xs font-semibold text-amber-900 shadow-sm hover:bg-amber-50"
                              >
                                <Sun size={14} className="text-amber-600" />
                                Or use the AI Project Architecte
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* No project assets - prompt to add assets in Asset tab first */
                    <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-gray-200 text-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center shadow-lg mx-auto mb-4">
                        <Network size={32} className="text-gray-400" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        No Equipment Yet
                      </h3>
                      <p className="text-gray-600 text-sm mb-6">
                        To create equipment, add project assets first in the Asset tab.
                      </p>
                      <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 mb-6">
                        <div className="flex items-start gap-3">
                          <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <AlertCircle size={16} className="text-white" />
                          </div>
                          <div className="text-left flex-1">
                            <h4 className="font-semibold text-amber-900 mb-1 text-sm">Next Step</h4>
                            <p className="text-xs text-amber-800">
                              Add project assets above, then return here to build your equipment hierarchy.
                            </p>
                          </div>
                        </div>
                      </div>
                      {onAddMasterAsset && (
                        <button
                          onClick={onAddMasterAsset}
                          className="btn-premium inline-flex items-center gap-2 px-4 py-2.5 text-[13px] font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 ease-out active:scale-[0.98]"
                        >
                          <Box size={18} />
                          Add Asset
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Assignment Mode Toolbar */}
            {assignmentMode && visualEquipmentList.length > 0 && (
              <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm">
                <div className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => {
                        setAssignmentMode(false);
                        clearSelection();
                      }}
                      className="btn-premium flex items-center gap-2 px-3 py-2 text-[13px] font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-all duration-200"
                    >
                      <X size={16} />
                      Cancel
                    </button>

                    <div className="h-6 w-px bg-gray-300" />

                    <span className="text-sm font-medium text-gray-700">
                      {selectedEquipmentIds.size} selected — descendants assigned on server
                    </span>

                    {selectedEquipmentIds.size > 0 && (
                      <>
                        <button
                          onClick={selectAllEquipment}
                          className="btn-premium text-[13px] text-blue-600 hover:text-blue-700 font-semibold transition-colors"
                        >
                          Select All
                        </button>
                        <button
                          onClick={clearSelection}
                          className="btn-premium text-[13px] text-slate-600 hover:text-slate-700 font-semibold transition-colors"
                        >
                          Clear
                        </button>
                      </>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-1.5 min-w-0 shrink-0">
                    {selectedIncludesRejectedResource && selectedEquipmentIds.size > 0 && (
                      <p
                        className="text-[11px] font-medium text-red-600 text-right max-w-xs leading-snug"
                        role="status"
                      >
                        Selection includes rejected assignments — confirm changes in the drawer, then use{' '}
                        <span className="font-semibold">Submit Review</span> in the approval flow to resubmit.
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowAssignmentDrawer(true)}
                      disabled={selectedEquipmentIds.size === 0}
                      title={
                        selectedIncludesRejectedResource && selectedEquipmentIds.size > 0
                          ? 'Update assignment, then use Submit Review to resubmit'
                          : undefined
                      }
                      className={`btn-premium flex items-center gap-2 px-4 py-2.5 text-[13px] rounded-xl font-semibold transition-all duration-200 ease-out active:scale-[0.98] ${
                        selectedEquipmentIds.size === 0
                          ? 'bg-gradient-to-r from-slate-400 to-slate-400 cursor-not-allowed text-white shadow-none'
                          : selectedIncludesRejectedResource
                            ? 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 text-white shadow-md hover:shadow-lg ring-2 ring-red-300'
                            : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white shadow-md hover:shadow-lg'
                      }`}
                    >
                      <UserCheck size={16} />
                      Assign to resource ({selectedEquipmentIds.size})
                    </button>
                  </div>
                </div>
              </div>
            )}

            {visualEquipmentList.length > 0 && (
              <div
                ref={scrollContainerRef}
                className="relative overflow-x-auto overflow-y-auto custom-scrollbar"
                style={{
                  width: `${Math.max(maxX + 200, 100)}px`,
                  height: `${Math.max(maxY + 100, 100)}px`,
                  minWidth: '100%',
                  minHeight: '100%',
                  zIndex: 0
                }}
              >
                <div
                  ref={treeContainerRef}
                  className="relative origin-top-left transition-transform duration-150 ease-out"
                  style={{
                    transform: `scale(${zoomLevel})`,
                    transformOrigin: '0 0',
                    width: `${Math.max(maxX + 200, 100)}px`,
                    height: `${Math.max(maxY + 100, 100)}px`,
                    padding: '20px'
                  }}
                >
                  {/* Engineering canvas (dot grid, inset) */}
                  <div
                    className="absolute pointer-events-none equipment-tree-canvas rounded-sm"
                    style={{
                      left: TREE_GRID.PADDING,
                      top: TREE_GRID.PADDING,
                      right: TREE_GRID.PADDING,
                      bottom: TREE_GRID.PADDING,
                      zIndex: 0
                    }}
                  />

                  <TreeConnections layoutNodes={layoutNodes} expandedNodes={expandedNodes} />
                  <TreeGroupBorders
                    layoutNodes={layoutNodes}
                    expandedNodes={expandedNodes}
                    visualEquipmentList={visualEquipmentList}
                  />

                  <div style={{ zIndex: 2 }}>
                    {layoutNodes.map(renderNode)}
                  </div>

                </div>
              </div>
            )}

          </div>
          </div>
          </section>

            {/* Add Bulk Equipment - Right Side Drawer (choose equipment, then configure bulk add) */}
            <BulkEquipmentDrawer
              open={addModalState.open}
              onClose={() =>
                setAddModalState({
                  ...addModalState,
                  open: false,
                  stringCount: undefined,
                  combinerBoxCount: undefined
                })
              }
              title={addModalState.ghost ? `Add Bulk Equipment - ${addModalState.ghost.assetName}` : 'Add Bulk Equipment'}
              addModalState={addModalState}
              setAddModalState={setAddModalState}
              isBulkCreatingEquipment={isBulkCreatingEquipment}
              onBulkCreate={handleBulkCreate}
              manufacturers={manufacturers}
              dropdownMakeAndModels={dropdownMakeAndModels}
              newBulkDocument={newBulkDocument}
              setNewBulkDocument={setNewBulkDocument}
              onAddBulkDocument={handleAddBulkDocument}
              onRemoveBulkDocument={handleRemoveBulkDocument}
              onOpenManufacturerCreator={() => setShowManufacturerCreator(true)}
              onOpenMakeAndModelCreator={() => {
                setSelectedManufacturerForModel(addModalState.manufacturer ?? '');
                setShowMakeAndModelCreator(true);
              }}
              equipmentList={equipmentList}
              projectAssets={projectAssets}
              visualEquipmentList={visualEquipmentList}
              availableAssets={availableAssets}
              getAssetTypeConfig={getAssetTypeConfig}
            />

            {/* Child Asset Selection - Right Drawer (Add Child Equipment) */}
            <RightDrawer
              open={childSelectionState.open}
              onClose={() => setChildSelectionState({ ...childSelectionState, open: false })}
              title={childSelectionState.parentId ? "Add Child Equipment" : "Select Asset to Add"}
              size="md"
            >
              <div className="space-y-4">
                {childSelectionState.parentId && (
                  <p className="text-sm text-gray-500 break-words">
                    Parent:{' '}
                    <span className="text-gray-700 font-medium">
                      {visualEquipmentList.find(e => e.id === childSelectionState.parentId)?.name}
                    </span>
                  </p>
                )}
                <p className="text-sm text-gray-600">
                  {childSelectionState.parentId ? "Select an asset type to add:" : "Select the asset type for bulk creation:"}
                </p>

                <div className="grid grid-cols-2 gap-3">
                  {projectAssets.map(asset => {
                    const config = getAssetTypeConfig(asset.name, availableAssets);
                    const Icon = config.icon;

                    const recommended = getRecommendedChildren(childSelectionState.parentType || '');
                    const isRecommended = recommended.some(rec => {
                      const recType = rec.assetType.toLowerCase();
                      const assetName = asset.name.toLowerCase();
                      return recType === assetName ||
                        (recType.includes('module') && assetName.includes('module')) ||
                        (recType.includes('string') && assetName.includes('string')) ||
                        (recType.includes('inverter') && assetName.includes('inverter')) ||
                        (recType.includes('transformer') && assetName.includes('transformer')) ||
                        assetName.includes(recType) ||
                        recType.includes(assetName);
                    });

                    return (
                      <button
                        key={asset._id}
                        onClick={() => {
                          if (isRecommended) {
                            const projectAsset = projectAssets.find(pa => pa.masterAssetId === asset.masterAssetId);
                            const matchingRec = recommended.find(rec => {
                              const recType = rec.assetType.toLowerCase();
                              const assetName = asset.name.toLowerCase();
                              return recType === assetName ||
                                (recType.includes('module') && assetName.includes('module')) ||
                                (recType.includes('string') && assetName.includes('string')) ||
                                (recType.includes('inverter') && assetName.includes('inverter')) ||
                                (recType.includes('transformer') && assetName.includes('transformer')) ||
                                assetName.includes(recType) ||
                                recType.includes(assetName);
                            });
                            const rec = matchingRec || (recommended[0] as StandardChildConfig) || { suggestedQuantity: 1, unitsPerParent: 1, namingPattern: '{asset}-{seq}' };

                            setAddModalState({
                              open: true,
                              ghost: {
                                masterAssetId: asset.masterAssetId,
                                projectAssetId: projectAsset?._id,
                                assetName: asset.name,
                                assetType: asset.name,
                                parentId: childSelectionState.parentId,
                                parentType: childSelectionState.parentType ?? undefined,
                                config: {
                                  unitsPerParent: rec.unitsPerParent || 1,
                                  namingPattern: rec.namingPattern || '{asset}-{seq}'
                                }
                              },
                              quantity: rec.suggestedQuantity || 1,
                              namingPattern: rec.namingPattern || '{asset}-{seq}',
                              documents: []
                            });
                            setChildSelectionState({ ...childSelectionState, open: false });
                          } else {
                            const projectAsset = projectAssets.find(pa => pa.masterAssetId === asset.masterAssetId);
                            setAddModalState({
                              open: true,
                              ghost: {
                                masterAssetId: asset.masterAssetId,
                                projectAssetId: projectAsset?._id,
                                assetName: asset.name,
                                assetType: asset.name,
                                parentId: childSelectionState.parentId,
                                parentType: childSelectionState.parentType ?? undefined,
                                config: {
                                  unitsPerParent: 1,
                                  namingPattern: '{asset}-{seq}'
                                }
                              },
                              quantity: 1,
                              namingPattern: '{asset}-{seq}',
                              documents: []
                            });
                            setChildSelectionState({ ...childSelectionState, open: false, selectedAsset: null });
                          }
                        }}
                        className={`btn-premium flex items-center gap-3 p-3 rounded-xl border text-left transition-all duration-200 ease-out ${isRecommended
                          ? 'border-emerald-200 bg-emerald-50/80 hover:border-emerald-300 hover:shadow-md'
                          : 'border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/80'
                          }`}
                      >
                        <div
                          className="w-10 h-10 flex items-center justify-center rounded-xl"
                          style={{
                            background: isRecommended ? '#dcfce7' : `linear-gradient(135deg, ${config.color}15, ${config.color}30)`,
                            color: isRecommended ? '#166534' : config.color
                          }}
                        >
                          <Icon size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-sm text-left break-words leading-snug">
                            {asset.name}
                          </h4>
                          {isRecommended && (
                            <span className="inline-block px-1.5 py-0.5 bg-green-200 text-green-800 text-[10px] rounded font-bold mt-1">
                              Recommended
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </RightDrawer>



            {/* Equipment Details Drawer */}


            {/* Equipment Assignment Drawer */}
            <EquipmentAssignmentDrawer
              isOpen={showAssignmentDrawer}
              onClose={() => setShowAssignmentDrawer(false)}
              projectId={project._id || project.id}
              selectedEquipmentIds={Array.from(selectedEquipmentIds).filter(id => !id.includes('::'))}
              equipmentList={equipmentList}
              onAssignmentComplete={() => {
                clearSelection();
                setAssignmentMode(false);
                fetchEquipment(); // Refresh to show updated assignments
                fetchAssignees(); // Refresh to get latest assignee names
                refetchPendingChanges(); // Surface any new pending head approvals
                if (onEquipmentUpdate) onEquipmentUpdate();
              }}
            />

            {/* Asset Checkpoints Drawer */}
            {/* Asset Checkpoints Drawer */}
            <ProjectAssetCheckpointDrawer
              open={showCheckpointsDrawer}
              onClose={() => {
                setShowCheckpointsDrawer(false);
                setSelectedAssetForCheckpoints(null);
              }}
              projectAsset={selectedAssetForCheckpoints}
              onRefresh={() => {
                // Refresh approval data so asset rejected/updated badges update after checkpoint change
                if (project?._id) {
                  fetchApprovalData();
                }
                // Notify parent so project progress header and asset list refresh (align hierarchy)
                if (onEquipmentUpdate) onEquipmentUpdate();
              }}
            />

            {/* Equipment Edit Modals */}


            <EquipmentEditDrawer
              open={showEquipmentEditDrawer}
              onClose={() => {
                setShowEquipmentEditDrawer(false);
                setCurrentEquipment(null);
              }}
              equipment={currentEquipment}
              manufacturers={manufacturers}
              makeAndModels={makeAndModels}
              onSuccess={handleEquipmentEditSuccess}
              onAddChild={(eq) => {
                setShowEquipmentEditDrawer(false);
                setCurrentEquipment(null);
                setChildSelectionState({
                  open: true,
                  parentId: eq._id || eq.id,
                  parentType: eq.assetType || eq.hierarchyLevel || '',
                  warningOpen: false,
                  selectedAsset: null
                });
              }}
              onAddCableChild={
                currentEquipment &&
                !isEquipmentOrDescendantsAssigned(String(currentEquipment._id || currentEquipment.id))
                  ? (eq, kind) => {
                      setShowEquipmentEditDrawer(false);
                      setCurrentEquipment(null);
                      openCableChildModal(
                        {
                          id: String(eq._id || eq.id || ''),
                          assetType: eq.assetType || eq.hierarchyLevel || 'Other',
                          parentEquipmentId: eq.parentId ?? null,
                          childEquipmentIds: [],
                          name: eq.equipmentName || eq.name || 'Unnamed',
                        },
                        kind
                      );
                    }
                  : undefined
              }
              onDeleteEquipment={
                currentEquipment &&
                !assignmentMode &&
                !isEquipmentOrDescendantsAssigned(String(currentEquipment._id || currentEquipment.id))
                  ? () => handleDeleteEquipment(String(currentEquipment._id || currentEquipment.id))
                  : undefined
              }
            />

            {/* Full View Drawer for Site Equipment Network */}
            <RightDrawer
              open={fullViewOpen}
              onClose={() => setFullViewOpen(false)}
              title="Site Equipment Network — Full View"
              size="full"
            >
              <div className="h-full w-full flex flex-col bg-slate-100/80 min-h-0">
                <div className="flex-shrink-0 flex flex-wrap items-center gap-3 border-b border-slate-200 bg-white px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-600 shadow-sm">
                      <Network size={16} className="text-white" />
                    </div>
                    <h3 className="text-sm font-semibold text-slate-900">Equipment Hierarchy</h3>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-800 tabular-nums">
                      {persistedEquipmentTotal.toLocaleString()} equipment
                    </span>
                    {persistedModuleTotal > 0 && (
                      <span className="rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800 tabular-nums">
                        {persistedModuleTotal.toLocaleString()} PV modules
                      </span>
                    )}
                    <span className="rounded-md border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-800 tabular-nums">
                      {visualEquipmentList.length.toLocaleString()} visible
                    </span>
                  </div>
                </div>
                <div className="flex-1 relative overflow-auto custom-scrollbar bg-slate-100/80 min-h-0 [contain:layout]">
                  {visualEquipmentList.length > 0 ? (
                    <div
                      className="relative"
                      style={{
                        width: `${Math.max(maxX + 200, 100)}px`,
                        height: `${Math.max(maxY + 100, 100)}px`,
                        minWidth: '100%',
                        minHeight: '100%',
                      }}
                    >
                      <div
                        className="relative origin-top-left transition-transform duration-150 ease-out"
                        style={{
                          transform: `scale(${zoomLevel})`,
                          transformOrigin: '0 0',
                          width: `${Math.max(maxX + 200, 100)}px`,
                          height: `${Math.max(maxY + 100, 100)}px`,
                          padding: '20px',
                        }}
                      >
                        <div
                          className="absolute pointer-events-none equipment-tree-canvas rounded-sm"
                          style={{
                            left: TREE_GRID.PADDING,
                            top: TREE_GRID.PADDING,
                            right: TREE_GRID.PADDING,
                            bottom: TREE_GRID.PADDING,
                            zIndex: 0,
                          }}
                        />
                        <TreeConnections layoutNodes={layoutNodes} expandedNodes={expandedNodes} />
                        <TreeGroupBorders
                          layoutNodes={layoutNodes}
                          expandedNodes={expandedNodes}
                          visualEquipmentList={visualEquipmentList}
                        />
                        <div style={{ zIndex: 2 }}>{layoutNodes.map(renderNode)}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full text-slate-400 text-sm">
                      No equipment to display.
                    </div>
                  )}
                </div>
              </div>
            </RightDrawer>

        </div>
      </div>

      {/* Zoom Controls - when equipment tree is visible */}
      {visualEquipmentList.length > 0 && (
        <ZoomControls
          zoomLevel={zoomLevel}
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          onReset={resetZoom}
          canZoomIn={canZoomIn}
          canZoomOut={canZoomOut}
        />
      )}

      {/* Confirmation Drawer */}
      <ConfirmationDrawer
        open={confirmationState.open}
        onClose={() => setConfirmationState(prev => ({ ...prev, open: false }))}
        onConfirm={confirmationState.onConfirm}
        title={confirmationState.title}
        message={confirmationState.message}
        variant={confirmationState.variant}
        loading={confirmationState.loading}
      />

      {/* String Grouping Suggestion Modal */}
      <RightDrawer
        open={stringGroupingState.open}
        onClose={() => {
          setStringGroupingState({ ...stringGroupingState, open: false });
        }}
        title="Suggested String Grouping"
        size="md"
      >
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">Recommended Organization</h4>
            <p className="text-sm text-blue-700">
              You're adding {stringGroupingState.totalModules} solar modules under a {stringGroupingState.parentType}.
              String and module counts can be stored on the inverter record (virtual strings in the tree, no separate
              string equipment or checkpoints).
            </p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Suggested Distribution</label>
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              {stringGroupingState.suggestedStrings.map((str, idx) => (
                <div key={idx} className="flex items-center justify-between bg-white rounded px-3 py-2 border border-gray-200">
                  <span className="font-medium text-gray-900">{str.stringName}</span>
                  <span className="text-sm text-gray-600">{str.moduleCount} modules</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              onClick={() => {
                // User declined, create modules directly without strings
                setStringGroupingState({ ...stringGroupingState, open: false });
                // Don't proceed with bulk create, user needs to click Add Assets again
              }}
              className="btn-premium flex-1 px-4 py-2.5 text-[13px] font-semibold text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 hover:border-slate-300 transition-all duration-200"
            >
              Skip (No Strings)
            </button>
            <button
              onClick={handleBulkCreateWithStrings}
              disabled={isBulkCreatingEquipment}
              className={`btn-premium flex-1 px-4 py-2.5 text-[13px] font-semibold text-white rounded-xl flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all duration-200 ease-out active:scale-[0.98] ${
                isBulkCreatingEquipment
                  ? 'bg-slate-400 cursor-not-allowed shadow-none'
                  : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500'
              }`}
            >
              {isBulkCreatingEquipment && <Loader2 size={16} className="animate-spin" />}
              {isBulkCreatingEquipment ? 'Saving...' : 'Save layout on inverter'}
            </button>
          </div>
        </div>
      </RightDrawer>

      {/* Manufacturer Creator Drawer */}
      <RightDrawer
        open={showManufacturerCreator}
        onClose={() => setShowManufacturerCreator(false)}
        title="Create New Manufacturer"
        size="lg"
      >
        <ManufacturerForm
          editingItem={null}
          onAdd={handleManufacturerCreated}
          onClose={() => setShowManufacturerCreator(false)}
        />
      </RightDrawer>

      {/* Make & Model Creator Drawer */}
      <RightDrawer
        open={showMakeAndModelCreator}
        onClose={() => setShowMakeAndModelCreator(false)}
        title="Create New Make & Model"
        size="lg"
      >
        <MakeAndModelForm
          editingItem={null}
          manufacturers={manufacturers}
          onAdd={handleMakeAndModelCreated}
          onClose={() => setShowMakeAndModelCreator(false)}
          onManufacturerAdded={async (newMfg: any) => {
            // Refresh manufacturers list when new manufacturer is created from within MakeAndModelForm
            const mfgData = await manufacturerService.manufacturerService.getManufacturers({ limit: 1000 });
            setManufacturers(mfgData.data || []);
          }}
        />
      </RightDrawer>

      <SolarPlantSetupWizard
        open={solarWizardOpen}
        onClose={() => setSolarWizardOpen(false)}
        projectId={projectId || ''}
        project={project}
        availableAssets={availableAssets}
        projectAssets={projectAssets}
        onCompleted={async () => {
          await refetchEquipment();
          onEquipmentUpdate?.();
        }}
      />
    </div>
  );
}
